import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { FiSave, FiClock, FiAlertTriangle, FiInfo } from "react-icons/fi";
import getApiBaseUrl from "../utils/api";
import FullscreenButton from "./FullscreenButton";
import IELTS_MOCK2_WRITING from "./ielts-mock2/writingData";
import IELTS_MOCK3_WRITING from "./ielts-mock3/writingData";
import IELTS_MOCK4_WRITING from "./ielts-mock4/writingData";
import IELTS_MOCK5_WRITING from "./ielts-mock5/writingData";
import IELTS_MOCK6_WRITING from "./ielts-mock6/writingData";
import IELTS_MOCK7_WRITING from "./ielts-mock7/writingData";
import IELTS_MOCK8_WRITING from "./ielts-mock8/writingData";
import IELTS_MOCK9_WRITING from "./ielts-mock9/writingData";

// ============================================================================
// API HELPER
// getApiBaseUrl() already ends with "/api", so paths here start with "/scoring/..."
// credentials: "include" is REQUIRED so the HttpOnly authToken cookie is sent
// to the backend (different port = cross-origin). Without it every call is a 401.
// ============================================================================
const apiFetch = (apiUrl, path, options = {}) =>
  fetch(`${apiUrl}${path}`, {
    credentials: "include",
    ...options,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      ...(options.headers || {})
    }
  });

// ============================================================================
// DATA
// ============================================================================
const IELTS_DATA_WRITING = {
  title: "Academic Writing Practice Test 1",
  tasks: [
    {
      id: "task1",
      title: "Task 1: Report (20 min)",
      prompt:
        "The diagram below shows how bricks are manufactured for the building industry. Summarise the information by selecting and reporting the main features, and make comparisons where relevant.",
      image: "/bricks.png",
      minWords: 150,
      keywords: ["brick", "manufactur", "process", "clay", "kiln", "produc", "stage", "diagram", "compar"],
      diagramType: "process"
    },
    {
      id: "task2",
      title: "Task 2: Essay (40 min)",
      prompt:
        "Compared to the past, more people are now trying to learn a foreign language to increase their chances of landing a better job in their native country or having better opportunities to work abroad. To what extent do you agree with this point of view? Give specific reasons and examples to support your opinion.",
      minWords: 250,
      keywords: ["language", "job", "work", "career", "opportunit", "abroad", "agree", "disagree", "learn"]
    }
  ]
};

const LINKING_WORDS = [
  "however", "moreover", "furthermore", "in addition", "therefore", "consequently",
  "for example", "for instance", "in conclusion", "on the other hand", "as a result",
  "in contrast", "similarly", "although", "despite", "nevertheless", "thus", "hence",
  "firstly", "secondly", "finally", "overall", "in summary", "to conclude", "meanwhile"
];

const STOPWORDS = new Set([
  "the", "a", "an", "is", "are", "was", "were", "and", "or", "but", "of", "to", "in", "on",
  "for", "with", "as", "by", "at", "from", "that", "this", "it", "be", "have", "has", "had",
  "not", "i", "you", "they", "we", "he", "she", "their", "its", "his", "her", "my", "your",
  "our", "which", "will", "would", "can", "could", "should", "been", "being", "do", "does",
  "did", "so", "if", "than", "then", "there", "these", "those", "also", "into", "about",
  "more", "most", "other"
]);

const OVERVIEW_MARKERS = ["overall", "in general", "it is clear that", "it can be seen that", "as can be seen", "in summary"];
const COMPARISON_MARKERS = ["compared to", "compared with", "whereas", "while", "in contrast", "on the other hand", "than", "similarly", "likewise"];
const OPINION_MARKERS = ["i believe", "i think", "in my opinion", "i agree", "i disagree", "from my perspective", "i would argue", "it is my view", "my view is"];
const EXAMPLE_MARKERS = ["for example", "for instance", "such as", "e.g.", "namely", "a good example"];
const CONCLUSION_MARKERS = ["in conclusion", "to conclude", "to summarise", "to summarize", "in summary", "overall,"];
const REFERENCING_MARKERS = ["this shows", "this means", "these factors", "such a", "this suggests", "this indicates", "this trend"];

const ELABORATION_MARKERS = [
  "this means", "this shows", "this suggests", "this indicates", "which means", "for example",
  "for instance", "as a result", "this is because", "specifically", "in other words",
  "this is due to", "as a consequence", "this leads to", "this results in"
];

const PROCESS_SEQUENCING_MARKERS = [
  "first,", "firstly", "second,", "secondly", "third,", "thirdly", "then,", "next,",
  "after that", "after this", "following this", "once", "subsequently", "finally",
  "at this stage", "at the final stage", "the first stage", "the next stage"
];

const STANCE_AGREE_MARKERS = [
  "i agree", "i support", "in favour of", "in favor of", "i am convinced", "i strongly agree",
  "i concur", "this view is correct", "i side with"
];

const STANCE_DISAGREE_MARKERS = [
  "i disagree", "i do not agree", "i don't agree", "i oppose", "against this view",
  "not convinced", "i strongly disagree", "this view is incorrect", "i cannot agree"
];

const KEY_FEATURE_MARKERS = [
  "highest", "lowest", "largest", "smallest", "majority", "minority", "significant proportion",
  "notably", "most notably", "in particular", "the most striking", "stands out", "by far",
  "the biggest", "the greatest"
];

const DATA_GROUPING_MARKERS = [
  "both", "the remaining", "in contrast", "unlike", "similarly", "whereas", "the other",
  "on the other hand", "respectively", "meanwhile", "conversely", "the rest of",
  "the majority of the remaining"
];

const TREND_MARKERS = [
  "increase", "increased", "increasing", "decrease", "decreased", "decreasing", "rise", "rose",
  "rising", "fall", "fell", "falling", "fluctuate", "fluctuated", "peak", "peaked", "plummet",
  "plummeted", "steadily", "dramatically", "sharply", "gradually", "remained stable",
  "remained constant", "level off", "levelled off", "surge", "surged", "decline", "declined",
  "climb", "climbed", "drop", "dropped"
];

const SYNONYM_GROUPS = [
  ["important", "significant", "crucial", "vital", "essential", "key"],
  ["increase", "rise", "grow", "climb", "surge", "escalate"],
  ["decrease", "decline", "drop", "fall", "plummet", "diminish"],
  ["show", "demonstrate", "indicate", "reveal", "illustrate", "highlight"],
  ["big", "large", "huge", "substantial", "considerable", "significant"],
  ["small", "minor", "slight", "negligible", "minimal"],
  ["believe", "think", "feel", "argue", "contend", "maintain"],
  ["problem", "issue", "challenge", "difficulty", "obstacle"],
  ["cause", "lead to", "result in", "bring about", "trigger"],
  ["many", "numerous", "a great many", "a large number of", "countless"],
  ["good", "beneficial", "advantageous", "favourable", "favorable"],
  ["bad", "harmful", "detrimental", "damaging", "adverse"],
  ["change", "shift", "transform", "alter", "modify"],
  ["help", "assist", "aid", "support", "facilitate"]
];

const WRITING_DATA_BY_MOCK = { 1: IELTS_DATA_WRITING, 2: IELTS_MOCK2_WRITING, 3: IELTS_MOCK3_WRITING, 4: IELTS_MOCK4_WRITING, 5: IELTS_MOCK5_WRITING, 6: IELTS_MOCK6_WRITING, 7: IELTS_MOCK7_WRITING, 8: IELTS_MOCK8_WRITING, 9: IELTS_MOCK9_WRITING };
// Prompt text of the mock a task belongs to, for the copied-from-prompt check.
const PROMPTS_TEXT_BY_MOCK = Object.fromEntries(
  Object.entries(WRITING_DATA_BY_MOCK).map(([m, d]) => [m, d.tasks.map((t) => t.prompt).join(" ")])
);

// ============================================================================
// HELPERS
// ============================================================================
const roundToHalfBand = (score) => Math.round(score * 2) / 2;
const clampBand = (score) => Math.max(1, Math.min(9, score));

const roundOverallBand = (score) => {
  const floor = Math.floor(score);
  const decimal = Number((score - floor).toFixed(2));
  if (decimal < 0.25) return floor;
  if (decimal < 0.75) return floor + 0.5;
  return floor + 1;
};

const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const normalizeForCompare = (text) =>
  (text || "").toLowerCase().replace(/[^a-z0-9\s]/g, " ").split(/\s+/).filter(Boolean);

const getNgrams = (tokens, n) => {
  const grams = new Set();
  for (let i = 0; i <= tokens.length - n; i++) {
    grams.add(tokens.slice(i, i + n).join(" "));
  }
  return grams;
};

const getCopyRatio = (responseText, sourceText) => {
  const responseTokens = normalizeForCompare(responseText);
  if (responseTokens.length < 5) return 0;
  const sourceTokens = normalizeForCompare(sourceText);
  const n = 5;
  const responseGrams = getNgrams(responseTokens, n);
  const sourceGrams = getNgrams(sourceTokens, n);
  if (responseGrams.size === 0) return 0;
  let matches = 0;
  responseGrams.forEach((g) => {
    if (sourceGrams.has(g)) matches++;
  });
  return matches / responseGrams.size;
};

const jaccard = (setA, setB) => {
  if (setA.size === 0 || setB.size === 0) return 0;
  let intersection = 0;
  setA.forEach((x) => {
    if (setB.has(x)) intersection++;
  });
  const union = setA.size + setB.size - intersection;
  return union === 0 ? 0 : intersection / union;
};

const contentWordSet = (text) =>
  new Set((text.toLowerCase().match(/[a-z']+/g) || []).filter((w) => !STOPWORDS.has(w)));

const splitSentences = (text) =>
  text.split(/[.!?]+/).map((s) => s.trim()).filter(Boolean);

const splitParagraphs = (text) =>
  text.split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean);

const getWordCount = (text) => {
  if (!text) return 0;
  return text.trim().split(/\s+/).filter(Boolean).length;
};

// ============================================================================
// LANGUAGETOOL (external service: NO credentials, NO auth cookie)
// ============================================================================
const LANGUAGETOOL_ENDPOINT = "https://api.languagetool.org/v2/check";

const GRAMMAR_CATEGORY_WEIGHTS = {
  GRAMMAR: 1.5,
  CONFUSED_WORDS: 1.3,
  COLLOCATIONS: 1.2,
  SEMANTICS: 1.0,
  TYPOS: 1.0,
  COMPOUNDING: 0.7,
  REDUNDANCY: 0.6,
  PUNCTUATION: 0.5,
  CASING: 0.5
};

const checkGrammarWithLanguageTool = async (text) => {
  if (!text || text.trim().length === 0) return [];

  // The free public API rate-limits (HTTP 429) and sometimes returns 5xx, and both
  // tasks are checked at the same moment. Retry a couple of times with a short,
  // slightly randomised delay before giving up.
  const MAX_ATTEMPTS = 3;

  for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
    try {
      const response = await fetch(LANGUAGETOOL_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams({ text, language: "en-US", enabledOnly: "false" })
      });

      if (response.ok) {
        const data = await response.json();
        return Array.isArray(data.matches) ? data.matches : [];
      }

      const retryable = response.status === 429 || response.status >= 500;
      if (!retryable || attempt === MAX_ATTEMPTS) {
        throw new Error(`LanguageTool API responded with status ${response.status}`);
      }
    } catch (err) {
      if (attempt === MAX_ATTEMPTS) {
        console.warn("LanguageTool grammar check failed, falling back to heuristic scoring", err);
        return null;
      }
    }

    await new Promise((resolve) => setTimeout(resolve, 1200 * attempt + Math.random() * 600));
  }

  return null;
};

const scoreGrammarFromLanguageTool = (matches, text) => {
  const words = getWordCount(text);
  if (words === 0) {
    return { band: 4, note: "No content to assess.", source: "languagetool" };
  }

  const relevantErrors = matches.filter(
    (m) => m.rule && GRAMMAR_CATEGORY_WEIGHTS[m.rule.category?.id] !== undefined
  );

  const weightedErrorSum = relevantErrors.reduce(
    (sum, m) => sum + (GRAMMAR_CATEGORY_WEIGHTS[m.rule.category?.id] || 1),
    0
  );

  const per100 = (weightedErrorSum / words) * 100;

  let band;
  if (per100 === 0) band = 8.5;
  else if (per100 < 1) band = 8;
  else if (per100 < 2) band = 7.5;
  else if (per100 < 3.5) band = 7;
  else if (per100 < 5.5) band = 6.5;
  else if (per100 < 8) band = 6;
  else if (per100 < 11) band = 5.5;
  else if (per100 < 15) band = 5;
  else if (per100 < 20) band = 4.5;
  else band = 4;

  band = roundToHalfBand(Math.min(band, 8.5));

  const countBy = (id) => relevantErrors.filter((m) => m.rule.category?.id === id).length;
  const grammarCount = countBy("GRAMMAR");
  const typoCount = countBy("TYPOS");
  const punctCount = countBy("PUNCTUATION");
  const confusedCount = countBy("CONFUSED_WORDS");

  let note;
  if (relevantErrors.length === 0) {
    note = "No grammar, spelling, or punctuation errors detected by automated checking.";
  } else {
    const parts = [];
    if (grammarCount) parts.push(`${grammarCount} grammar issue${grammarCount > 1 ? "s" : ""} (high severity)`);
    if (confusedCount) parts.push(`${confusedCount} confused word${confusedCount > 1 ? "s" : ""} (high severity)`);
    if (typoCount) parts.push(`${typoCount} spelling issue${typoCount > 1 ? "s" : ""}`);
    if (punctCount) parts.push(`${punctCount} punctuation issue${punctCount > 1 ? "s" : ""} (low severity)`);
    const otherCount = relevantErrors.length - grammarCount - typoCount - punctCount - confusedCount;
    if (otherCount > 0) parts.push(`${otherCount} other mechanical issue${otherCount > 1 ? "s" : ""}`);
    note = `Detected ${relevantErrors.length} error(s), severity-weighted to ~${per100.toFixed(1)} per 100 words: ${parts.join(", ")}.`;
  }

  return {
    band,
    note: `${note} Checked automatically via LanguageTool with severity weighting.`,
    source: "languagetool"
  };
};

const scoreGrammarHeuristic = (text) => {
  const sentences = splitSentences(text);
  if (sentences.length === 0) {
    return { band: 4, note: "No sentences to assess.", source: "heuristic" };
  }

  const lengths = sentences.map((s) => s.split(/\s+/).filter(Boolean).length);
  const avgLen = lengths.reduce((a, b) => a + b, 0) / lengths.length;
  const variance = lengths.reduce((sum, l) => sum + Math.pow(l - avgLen, 2), 0) / lengths.length;
  const stdDev = Math.sqrt(variance);

  let varietyScore;
  if (stdDev < 3) varietyScore = 5;
  else if (stdDev < 6) varietyScore = 6.5;
  else varietyScore = 7.5;

  const punctuationScore = /[;:]/.test(text) ? 7 : 5.5;
  const band = roundToHalfBand((varietyScore + punctuationScore) / 2);

  return {
    band,
    note: "⚠️ Automated grammar checking was unavailable, so this is a rough estimate from sentence structure only (not real error detection).",
    source: "heuristic"
  };
};

// ============================================================================
// GRAMMAR RANGE
// ============================================================================
const GRAMMAR_STRUCTURES = {
  complexClauses: /\b(because|although|though|since|while|whereas|if|unless|when|whenever|which|who|whom|that)\b/gi,
  passiveVoice: /\b(is|are|was|were|been|being)\s+\w+ed\b/gi,
  perfectTense: /\b(has|have|had)\s+(been\s+)?\w+ed\b/gi,
  modals: /\b(would|could|might|should|must|may|can)\b/gi,
  conditionals: /\bif\s+.+?,.+?(would|will|could|might)\b/gi,
  gerundsInfinitives: /\b(to \w+ing|by \w+ing|\w+ing\s+to)\b/gi,
  comparatives: /\b(\w+er than|more \w+ than|less \w+ than|the most \w+|the \w+est)\b/gi,
  relativeClausesNonDefining: /,\s*(which|who|whom)\b/gi
};

const scoreGrammarRange = (text) => {
  if (!text || text.trim().length === 0) {
    return { band: 4, note: "No content to assess for grammatical range.", structuresUsed: [] };
  }

  // match() with a /g regex does not depend on lastIndex, so this is safe to reuse
  const structuresUsed = Object.entries(GRAMMAR_STRUCTURES)
    .filter(([, pattern]) => (text.match(pattern) || []).length > 0)
    .map(([name]) => name);

  const count = structuresUsed.length;

  let band;
  if (count === 0) band = 4.5;
  else if (count <= 2) band = 5.5;
  else if (count <= 4) band = 6.5;
  else if (count <= 6) band = 7.5;
  else band = 8.5;

  const note =
    count === 0
      ? "Only simple sentence structures detected — try adding complex sentences (because/although/which clauses), passives, or conditionals."
      : `Detected ${count} distinct structure type(s) in use (e.g. ${structuresUsed
          .slice(0, 3)
          .join(", ")}), showing ${count >= 5 ? "strong" : count >= 3 ? "moderate" : "limited"} grammatical range.`;

  return { band: roundToHalfBand(band), note, structuresUsed };
};

// No /g flag here on purpose: a global regex used with .test() keeps lastIndex
// between calls and gives wrong results.
const SUBORDINATORS =
  /\b(because|although|though|since|while|whereas|if|unless|when|whenever|which|who|that|as|after|before|until)\b/i;

const scoreComplexSentenceRatio = (text) => {
  const sentences = splitSentences(text);
  if (sentences.length === 0) {
    return { band: 4, note: "No sentences to assess.", ratio: 0 };
  }

  const complexCount = sentences.filter(
    (s) => SUBORDINATORS.test(s) || (s.match(/,/g) || []).length >= 2
  ).length;

  const ratio = complexCount / sentences.length;

  let band;
  if (ratio < 0.15) band = 5;
  else if (ratio < 0.3) band = 6;
  else if (ratio < 0.5) band = 7;
  else if (ratio < 0.7) band = 7.5;
  else band = 6.5;

  const note =
    ratio < 0.3
      ? `Only ~${Math.round(ratio * 100)}% of sentences use a complex structure — mix in more subordinate clauses for a higher grammar band.`
      : ratio > 0.7
      ? `Almost every sentence (${Math.round(ratio * 100)}%) is complex — some simple sentences for contrast/emphasis would read more naturally.`
      : `Good mix: ~${Math.round(ratio * 100)}% of sentences use complex structures.`;

  return { band: roundToHalfBand(band), note, ratio: Number(ratio.toFixed(2)) };
};

// ============================================================================
// COLLOCATIONS
// ============================================================================
const COMMON_MISCOLLOCATIONS = [
  { pattern: /\bmake (a |an )?research\b/gi, fix: "do research" },
  { pattern: /\bdiscuss about\b/gi, fix: "discuss (no preposition needed)" },
  { pattern: /\baccording to me\b/gi, fix: "in my opinion / I believe" },
  { pattern: /\bpay attention on\b/gi, fix: "pay attention to" },
  { pattern: /\bcapable to\b/gi, fix: "capable of" },
  { pattern: /\bdepend of\b/gi, fix: "depend on" },
  { pattern: /\bin the other hand\b/gi, fix: "on the other hand" },
  { pattern: /\bincrease day by day\b/gi, fix: "increase steadily / continually" },
  { pattern: /\bnowadays days\b/gi, fix: "nowadays" },
  { pattern: /\bmore and more increase\b/gi, fix: "increase steadily / rise" },
  { pattern: /\bmake a big role\b/gi, fix: "play a big/significant role" },
  { pattern: /\bincrease the number of\b.*\bday by day\b/gi, fix: "a growing/increasing number of" }
];

const ACADEMIC_COLLOCATIONS = [
  /\bplay(s|ed)? a (crucial|significant|vital|key|major) role\b/gi,
  /\bhave a (profound|significant|positive|negative|detrimental) (impact|effect) on\b/gi,
  /\bdraw(s|n)? a conclusion\b/gi,
  /\bgive(s)? rise to\b/gi,
  /\bin light of\b/gi,
  /\bshed(s)? light on\b/gi,
  /\ba wide range of\b/gi,
  /\ba significant (number|proportion) of\b/gi,
  /\bon the whole\b/gi,
  /\bfar outweigh(s)?\b/gi,
  /\bgive(s)? priority to\b/gi,
  /\bstrike(s)? a balance\b/gi,
  /\bbridge(s)? the gap\b/gi,
  /\ba double-edged sword\b/gi,
  /\bcontribut(e|es|ed) (significantly |greatly )?to\b/gi
];

const scoreCollocations = (text) => {
  if (!text || text.trim().length === 0) {
    return { band: 4, note: "No content to assess.", miscollocations: [], goodCollocations: 0 };
  }

  const miscollocationHits = [];
  COMMON_MISCOLLOCATIONS.forEach(({ pattern, fix }) => {
    const matches = text.match(pattern);
    if (matches) miscollocationHits.push({ found: matches[0], fix });
  });

  const goodCollocationCount = ACADEMIC_COLLOCATIONS.reduce(
    (count, pattern) => count + (text.match(pattern)?.length || 0),
    0
  );

  let band = 6;
  band += Math.min(goodCollocationCount, 4) * 0.3;
  band -= miscollocationHits.length * 0.4;
  band = roundToHalfBand(clampBand(band));

  let note;
  if (miscollocationHits.length > 0) {
    const examples = miscollocationHits
      .slice(0, 3)
      .map((h) => `"${h.found.trim()}" → ${h.fix}`)
      .join("; ");
    note = `${miscollocationHits.length} unnatural word pairing(s) detected, e.g. ${examples}.`;
  } else if (goodCollocationCount > 0) {
    note = `Uses ${goodCollocationCount} natural academic collocation(s), which supports a higher lexical band.`;
  } else {
    note = "No strong collocation errors or standout academic collocations detected.";
  }

  return { band, note, miscollocations: miscollocationHits, goodCollocations: goodCollocationCount };
};

// ============================================================================
// SYNONYM VARIETY
// ============================================================================
const scoreSynonymVariety = (text) => {
  const lower = text.toLowerCase();
  let groupsUsed = 0;
  let groupsVaried = 0;
  const flatWords = [];

  SYNONYM_GROUPS.forEach((group) => {
    const membersUsed = group.filter((w) => new RegExp(`\\b${escapeRegExp(w)}\\b`, "i").test(lower));
    if (membersUsed.length > 0) {
      groupsUsed++;
      if (membersUsed.length >= 2) {
        groupsVaried++;
      } else {
        const count = (lower.match(new RegExp(`\\b${escapeRegExp(membersUsed[0])}\\b`, "gi")) || []).length;
        if (count >= 3) flatWords.push(membersUsed[0]);
      }
    }
  });

  const varietyRatio = groupsUsed > 0 ? groupsVaried / groupsUsed : 0;

  let band;
  if (groupsUsed === 0) band = 6;
  else if (varietyRatio < 0.2) band = 5;
  else if (varietyRatio < 0.4) band = 6;
  else if (varietyRatio < 0.6) band = 7;
  else band = 8;

  let note;
  if (flatWords.length > 0) {
    note = `Repeats the same word (${flatWords
      .slice(0, 3)
      .join(", ")}) multiple times where a synonym would show more range — e.g. alternate "increase" with "rise"/"grow", or "important" with "significant"/"crucial".`;
  } else if (groupsUsed === 0) {
    note = "Not enough recurring concepts detected to assess paraphrase/synonym variety.";
  } else {
    note = `Good paraphrase variety — ${groupsVaried}/${groupsUsed} recurring concept group(s) used more than one synonym.`;
  }

  return { band: roundToHalfBand(band), note, groupsUsed, groupsVaried };
};

// ============================================================================
// IDEA DEVELOPMENT
// ============================================================================
const scoreIdeaDevelopment = (text) => {
  const paragraphs = splitParagraphs(text);
  if (paragraphs.length === 0) {
    return {
      band: 4,
      note: "No paragraphs to assess for idea development.",
      avgSentencesPerParagraph: 0,
      elaborationRatio: 0
    };
  }

  const bodyParagraphs = paragraphs.length >= 3 ? paragraphs.slice(1, -1) : paragraphs;
  const paragraphsToScore = bodyParagraphs.length > 0 ? bodyParagraphs : paragraphs;

  const sentenceCounts = paragraphsToScore.map((p) => splitSentences(p).length);
  const avgSentences = sentenceCounts.reduce((a, b) => a + b, 0) / sentenceCounts.length;

  const elaboratedCount = paragraphsToScore.filter((p) => {
    const lower = p.toLowerCase();
    return ELABORATION_MARKERS.some((m) => lower.includes(m));
  }).length;

  const elaborationRatio = elaboratedCount / paragraphsToScore.length;

  let lengthScore;
  if (avgSentences < 2) lengthScore = 4.5;
  else if (avgSentences < 3) lengthScore = 5.5;
  else if (avgSentences < 4) lengthScore = 6.5;
  else if (avgSentences < 5) lengthScore = 7.5;
  else lengthScore = 8;

  let elaborationScore;
  if (elaborationRatio < 0.3) elaborationScore = 4.5;
  else if (elaborationRatio < 0.6) elaborationScore = 6;
  else if (elaborationRatio < 0.9) elaborationScore = 7.5;
  else elaborationScore = 8.5;

  const band = roundToHalfBand((lengthScore + elaborationScore) / 2);

  let note;
  if (lengthScore <= 5.5 && elaborationScore <= 6) {
    note = "Ideas are underdeveloped — most paragraphs state a point without explaining it, giving a reason, or an example.";
  } else if (lengthScore <= 5.5) {
    note = "Paragraphs are quite short — try adding a sentence of explanation or an example after each main point.";
  } else if (elaborationScore <= 6) {
    note = "Points are reasonably long but often not explicitly linked to a reason or example — add phrases like 'this is because' or 'for example'.";
  } else {
    note = "Ideas are well extended with explanation and examples, not just stated.";
  }

  return {
    band,
    note,
    avgSentencesPerParagraph: Number(avgSentences.toFixed(1)),
    elaborationRatio: Number(elaborationRatio.toFixed(2))
  };
};

// ============================================================================
// EXAMPLE QUALITY
// ============================================================================
const scoreExampleQuality = (text) => {
  const lower = text.toLowerCase();
  const foundMarkers = EXAMPLE_MARKERS.filter((m) => lower.includes(m));

  if (foundMarkers.length === 0) {
    return {
      band: 4.5,
      note: "No examples detected — support your points with specific, concrete examples.",
      exampleCount: 0,
      specificCount: 0
    };
  }

  let specificCount = 0;

  foundMarkers.forEach((marker) => {
    const idx = lower.indexOf(marker);
    const windowText = text.slice(idx, idx + 140);
    const hasNumber = /\d/.test(windowText);
    const hasProperNoun = /\b[A-Z][a-z]{2,}\b/.test(windowText.slice(marker.length));
    const hasConcreteNoun =
      /\b(company|country|study|report|survey|government|university|city|percent|per cent)\b/i.test(windowText);
    if (hasNumber || hasProperNoun || hasConcreteNoun) specificCount++;
  });

  const specificityRatio = specificCount / foundMarkers.length;

  let band;
  if (specificityRatio === 0) band = 5;
  else if (specificityRatio < 0.5) band = 6;
  else if (specificityRatio < 1) band = 7;
  else band = 8;

  const note =
    specificityRatio < 0.5
      ? `${foundMarkers.length} example marker(s) found, but most are followed by vague statements rather than concrete details (names, numbers, specific situations).`
      : `${specificCount}/${foundMarkers.length} example(s) include concrete, specific detail rather than generic statements.`;

  return { band: roundToHalfBand(band), note, exampleCount: foundMarkers.length, specificCount };
};

// ============================================================================
// POSITION CONSISTENCY
// ============================================================================
const scorePositionConsistency = (text) => {
  const paragraphs = splitParagraphs(text);
  if (paragraphs.length === 0) {
    return { band: 5, note: "No content to assess for position consistency.", consistent: null };
  }

  const introText = paragraphs[0].toLowerCase();
  const conclusionText = (paragraphs.length > 1 ? paragraphs[paragraphs.length - 1] : paragraphs[0]).toLowerCase();

  const stanceOf = (t) => {
    const agree = STANCE_AGREE_MARKERS.some((m) => t.includes(m));
    const disagree = STANCE_DISAGREE_MARKERS.some((m) => t.includes(m));
    if (agree && !disagree) return "agree";
    if (disagree && !agree) return "disagree";
    if (agree && disagree) return "mixed";
    return "none";
  };

  const introStance = stanceOf(introText);
  const conclusionStance = stanceOf(conclusionText);

  let band;
  let note;
  let consistent;

  if (introStance === "none" && conclusionStance === "none") {
    band = 5;
    note = "No clear, explicit position detected in the introduction or conclusion — state your opinion directly (e.g. 'I agree that...').";
    consistent = null;
  } else if (introStance === "none" || conclusionStance === "none") {
    band = 5.5;
    note = "A position is stated in only one part of the essay (introduction or conclusion, not both) — restate your position clearly in both.";
    consistent = null;
  } else if (introStance === conclusionStance && introStance !== "mixed") {
    band = 7.5;
    note = "Position is stated clearly and held consistently from introduction to conclusion.";
    consistent = true;
  } else {
    band = 4;
    note = "The position in the conclusion appears to contradict (or blur) the position stated in the introduction — keep your stance consistent throughout.";
    consistent = false;
  }

  return { band: roundToHalfBand(band), note, consistent };
};

// ============================================================================
// TOPIC SENTENCES
// ============================================================================
const scoreTopicSentences = (text) => {
  const paragraphs = splitParagraphs(text);
  const body = paragraphs.length >= 3 ? paragraphs.slice(1, -1) : paragraphs;

  if (body.length === 0) {
    return { band: 5, note: "No body paragraphs to assess for topic sentences.", ratio: 0 };
  }

  let goodTopicSentences = 0;

  body.forEach((p) => {
    const sentences = splitSentences(p);
    if (sentences.length < 2) return;
    const first = contentWordSet(sentences[0]);
    const rest = contentWordSet(sentences.slice(1).join(" "));
    if (jaccard(first, rest) > 0.08) goodTopicSentences++;
  });

  const ratio = goodTopicSentences / body.length;

  let band;
  if (ratio < 0.3) band = 5;
  else if (ratio < 0.6) band = 6.5;
  else band = 7.5;

  const note =
    ratio < 0.5
      ? "Some paragraphs don't open with a sentence that clearly signals what the paragraph is about — start each body paragraph with a sentence stating its main point."
      : "Most paragraphs open with a clear topic sentence that previews the paragraph's content.";

  return { band: roundToHalfBand(band), note, ratio: Number(ratio.toFixed(2)) };
};

// ============================================================================
// PARAGRAPH UNITY
// ============================================================================
const scoreParagraphUnity = (text) => {
  const paragraphs = splitParagraphs(text);
  if (paragraphs.length === 0) {
    return { band: 5, note: "No paragraphs to assess for unity.", avgOverlap: 0 };
  }

  const paragraphScores = paragraphs
    .map((p) => {
      const sentences = splitSentences(p);
      if (sentences.length < 2) return null;
      let total = 0;
      let pairs = 0;
      for (let i = 0; i < sentences.length - 1; i++) {
        total += jaccard(contentWordSet(sentences[i]), contentWordSet(sentences[i + 1]));
        pairs++;
      }
      return pairs > 0 ? total / pairs : null;
    })
    .filter((s) => s !== null);

  if (paragraphScores.length === 0) {
    return { band: 5, note: "Paragraphs too short (single sentence) to assess unity.", avgOverlap: 0 };
  }

  const avgOverlap = paragraphScores.reduce((a, b) => a + b, 0) / paragraphScores.length;

  let band;
  if (avgOverlap < 0.04) band = 5;
  else if (avgOverlap < 0.09) band = 6.5;
  else band = 7.5;

  const note =
    avgOverlap < 0.06
      ? "Sentences within paragraphs sometimes feel loosely connected — make sure every sentence in a paragraph clearly relates back to that paragraph's main idea."
      : "Sentences within each paragraph stay on-topic and connect well to each other.";

  return { band: roundToHalfBand(band), note, avgOverlap: Number(avgOverlap.toFixed(3)) };
};

// ============================================================================
// TASK 1 SPECIFIC
// ============================================================================
const scoreTask1Specific = (text, task) => {
  const lower = text.toLowerCase();
  const notes = [];
  let adjustment = 0;

  if (task.diagramType === "process") {
    const seqHits = PROCESS_SEQUENCING_MARKERS.filter((m) => lower.includes(m)).length;
    const hasOpinion = OPINION_MARKERS.some((m) => lower.includes(m));
    const passiveMatches = lower.match(/\b(is|are|was|were|been|being)\s+\w+ed\b/g) || [];

    if (seqHits === 0) {
      adjustment -= 0.5;
      notes.push("No process sequencing language detected (first, then, next, after this, finally) — a process diagram needs clear stage markers.");
    } else if (seqHits >= 3) {
      adjustment += 0.5;
      notes.push("Good use of sequencing language to mark stages of the process.");
    }

    if (hasOpinion) {
      adjustment -= 1;
      notes.push("Personal opinion language detected — Task 1 is a factual report and should not include your opinion.");
    }

    if (passiveMatches.length >= 2) {
      adjustment += 0.3;
      notes.push("Good use of the passive voice, appropriate for describing a manufacturing process.");
    } else {
      notes.push("Consider using more passive voice (e.g. 'the clay is shaped') — it's standard for describing processes.");
    }
  }

  if (["line", "bar", "mixed", "table"].includes(task.diagramType)) {
    const trendHits = TREND_MARKERS.filter((m) => lower.includes(m)).length;
    if (trendHits === 0) {
      adjustment -= 0.5;
      notes.push("No trend/change language detected (increase, decrease, fluctuate, peak, steadily...) — describe how values change, not just their final state.");
    } else if (trendHits >= 4) {
      adjustment += 0.5;
      notes.push(`Good use of trend language (${trendHits} instance(s)) to describe how the data changes.`);
    }
  }

  const keyFeatureHits = KEY_FEATURE_MARKERS.filter((m) => lower.includes(m)).length;
  if (keyFeatureHits === 0) {
    adjustment -= 0.3;
    notes.push("No language highlighting standout/key features detected (highest, lowest, majority, notably...) — Task 1 should select and emphasise the MAIN features, not describe every data point equally.");
  } else {
    adjustment += 0.3;
    notes.push(`Highlights ${keyFeatureHits} standout feature(s) rather than listing all data points equally.`);
  }

  const groupingHits = DATA_GROUPING_MARKERS.filter((m) => lower.includes(m)).length;
  if (groupingHits === 0) {
    adjustment -= 0.2;
    notes.push("Little evidence of grouping similar data points together (e.g. 'both X and Y', 'unlike Z') — grouping related figures makes the report more analytical than a simple list.");
  } else {
    adjustment += 0.2;
    notes.push("Groups related data points together rather than listing them one by one.");
  }

  return { adjustment, note: notes.join(" "), keyFeatures: keyFeatureHits, dataGrouping: groupingHits };
};

// ============================================================================
// REFERENCE CONTENT CHECK (server route: /api/scoring/writing-validate)
// The server compares the response with saved reference points for this
// mock/task (key facts and trends for Task 1; question parts, accepted
// argument types and position for Task 2) using fixed keyword, synonym and
// figure rules. No AI service is involved, so it never waits on a quota and
// gives the same result for the same essay. Returns null if unavailable.
// ============================================================================
const REFERENCE_TIMEOUT_MS = 15000;

const fetchReferenceCoverage = async (text, task, apiUrl) => {
  if (!text || !text.trim()) return null;

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), REFERENCE_TIMEOUT_MS);

  try {
    const res = await apiFetch(apiUrl, "/scoring/writing-validate", {
      method: "POST",
      body: JSON.stringify({ essay: text, taskId: task.id, mock: task.mock || 1 }),
      signal: controller.signal
    });
    if (!res.ok) throw new Error(`Reference check responded ${res.status}`);

    const data = await res.json();
    if (typeof data?.contentCoverage !== "number" || !Array.isArray(data?.checklist)) {
      throw new Error("Malformed reference check response");
    }
    return data;
  } catch (err) {
    console.warn("Reference content check unavailable, using rule-based scoring only", err);
    return null;
  } finally {
    clearTimeout(timeout);
  }
};

// Content coverage (0-1) -> a Task Achievement/Response estimate used
// alongside the rule-based checks. Full coverage maps to 8.5, not 9: covering
// the expected points does not prove the ideas are well developed.
const coverageToBand = (coverage) => roundToHalfBand(clampBand(4 + 4.5 * coverage));

// ============================================================================
// ORIGINALITY
// ============================================================================
const TEMPLATE_SIMILARITY_THRESHOLD = 0.9;
const PLAGIARISM_SIMILARITY_THRESHOLD = 0.85;

const checkTemplateSimilarity = async (text, apiUrl) => {
  if (!text || text.trim().length < 40) return null;

  try {
    const res = await apiFetch(apiUrl, "/scoring/template-check", {
      method: "POST",
      body: JSON.stringify({ essay: text })
    });

    if (!res.ok) throw new Error(`Template-check endpoint responded ${res.status}`);

    const data = await res.json();
    if (data?.skipped) return null; // server has no template check configured
    if (typeof data?.similarity !== "number") {
      throw new Error("Malformed template-check response");
    }

    return {
      isTemplate: !!data.isTemplate || data.similarity >= TEMPLATE_SIMILARITY_THRESHOLD,
      similarity: data.similarity,
      matchedSource: data.matchedSource || null
    };
  } catch (err) {
    console.warn("Template-similarity check unavailable, skipping", err);
    return null;
  }
};

// Requires the server route POST /api/scoring/plagiarism-check (routes/plagiarismCheck.js).
// If the route is missing or the server has no reference texts, this returns null (skipped).
const checkEmbeddingPlagiarism = async (text, apiUrl) => {
  if (!text || text.trim().length < 40) return null;

  try {
    const res = await apiFetch(apiUrl, "/scoring/plagiarism-check", {
      method: "POST",
      body: JSON.stringify({ essay: text })
    });

    if (!res.ok) throw new Error(`Plagiarism-check endpoint responded ${res.status}`);

    const data = await res.json();
    if (data?.skipped) return null; // server has no plagiarism check configured
    if (typeof data?.similarity !== "number") {
      throw new Error("Malformed plagiarism-check response");
    }

    return {
      isPlagiarized: !!data.isPlagiarized || data.similarity >= PLAGIARISM_SIMILARITY_THRESHOLD,
      similarity: data.similarity,
      matchedSource: data.matchedSource || null
    };
  } catch (err) {
    console.warn("Embedding-based plagiarism check unavailable, skipping", err);
    return null;
  }
};

// ============================================================================
// CALIBRATION
// ============================================================================
const fetchScoringCalibration = async (apiUrl) => {
  try {
    const res = await apiFetch(apiUrl, "/scoring/calibration", { method: "GET" });
    if (!res.ok) throw new Error(`Calibration endpoint responded ${res.status}`);
    const data = await res.json();
    // The server returns zero offsets with sampleSize 0 when no human-scored data
    // exists yet. Treat that as "no calibration" so the UI doesn't claim it was calibrated.
    if (!data || data.sampleSize === 0) return null;
    return data;
  } catch (err) {
    console.warn("No scoring calibration available, using raw auto-scores", err);
    return null;
  }
};

// ============================================================================
// CONFIDENCE
// ============================================================================
const computeConfidence = ({
  reference, taBase, grammarSource,
  wordCount, minWords, copied, template, plagiarism
}) => {
  let points = 0;
  const maxPoints = 100;
  const reasons = [];

  if (reference) {
    points += 35;
    reasons.push("Content checked against saved reference points for this question");
  } else {
    reasons.push("Reference content check unavailable — relying on general rule-based checks only");
  }

  if (grammarSource === "languagetool") {
    points += 20;
    reasons.push("Real grammar error-checking succeeded");
  } else {
    reasons.push("Grammar checking fell back to a rough estimate");
  }

  if (reference) {
    const diff = Math.abs(coverageToBand(reference.contentCoverage) - taBase.band);
    if (diff <= 1) {
      points += 25;
      reasons.push("Reference coverage and general task checks agree");
    } else if (diff <= 2) {
      points += 12;
      reasons.push("Reference coverage and general task checks partly disagree");
    } else {
      reasons.push("Reference coverage and general task checks disagree");
    }
  } else {
    points += 10;
  }

  if (wordCount >= minWords) {
    points += 10;
    reasons.push("Meets minimum word count");
  } else {
    reasons.push("Below minimum word count — score is less reliable at this length");
  }

  if (copied || template?.isTemplate || plagiarism?.isPlagiarized) {
    points = Math.min(points, 40);
    reasons.push("Originality flag reduces confidence in a standard band interpretation");
  } else {
    points += 10;
  }

  const pct = Math.max(5, Math.min(100, Math.round((points / maxPoints) * 100)));
  const label = pct >= 80 ? "High" : pct >= 50 ? "Medium" : "Low";

  return { pct, label, reasons };
};

// ============================================================================
// PURE SCORERS (moved out of the component: they don't use component state)
// ============================================================================
const scoreTaskAchievement = (text, task) => {
  const words = getWordCount(text);
  const lower = text.toLowerCase();

  let lengthScore;
  if (words < task.minWords * 0.6) lengthScore = 4;
  else if (words < task.minWords * 0.8) lengthScore = 5;
  else if (words < task.minWords) lengthScore = 5.5;
  else if (words < task.minWords * 1.6) lengthScore = 7;
  else lengthScore = 6.5;

  const hits = task.keywords.filter((k) => lower.includes(k)).length;
  const relevance = hits / task.keywords.length;

  let relevanceScore;
  if (relevance < 0.2) relevanceScore = 4;
  else if (relevance < 0.4) relevanceScore = 5;
  else if (relevance < 0.6) relevanceScore = 6;
  else if (relevance < 0.8) relevanceScore = 7;
  else relevanceScore = 8;

  let structureScore;
  let structureNote;

  if (task.id === "task1") {
    const hasOverview = OVERVIEW_MARKERS.some((m) => lower.includes(m));
    const hasComparison = COMPARISON_MARKERS.some((m) => lower.includes(m));
    const hasData = /\d/.test(text) || lower.includes("per cent") || lower.includes("percent");
    const featuresPresent = [hasOverview, hasComparison, hasData].filter(Boolean).length;

    structureScore = featuresPresent === 3 ? 8 : featuresPresent === 2 ? 6.5 : featuresPresent === 1 ? 5 : 4;

    structureNote = !hasOverview
      ? "No clear overview statement detected — Task 1 needs one sentence summarising the main trend before the details."
      : !hasComparison
      ? "Few comparisons detected — contrast the data points, don't just list them."
      : "Includes an overview and comparisons, as required for Task 1.";
  } else {
    const hasPosition = OPINION_MARKERS.some((m) => lower.includes(m));
    const hasExamples = EXAMPLE_MARKERS.some((m) => lower.includes(m));
    const hasConclusion = CONCLUSION_MARKERS.some((m) => lower.includes(m));
    const featuresPresent = [hasPosition, hasExamples, hasConclusion].filter(Boolean).length;

    structureScore = featuresPresent === 3 ? 8 : featuresPresent === 2 ? 6.5 : featuresPresent === 1 ? 5 : 4;

    structureNote = !hasPosition
      ? "No clear position detected — state your opinion explicitly and keep it consistent throughout."
      : !hasExamples
      ? "Few concrete examples detected — support your ideas with specific examples."
      : !hasConclusion
      ? "No conclusion detected — end with a clear closing statement."
      : "Clear position, examples, and conclusion all present.";
  }

  const band = roundToHalfBand((lengthScore + relevanceScore + structureScore) / 3);

  return {
    band,
    note: `${relevance < 0.4 ? "Response may not fully address the prompt topic. " : ""}${structureNote}`
  };
};

const scoreCoherence = (text) => {
  const paragraphs = text.split(/\n\s*\n/).filter((p) => p.trim().length > 0);
  const lower = text.toLowerCase();

  const linkerCounts = LINKING_WORDS.map((w) => {
    const re = new RegExp(escapeRegExp(w), "g");
    return { word: w, count: (lower.match(re) || []).length };
  }).filter((l) => l.count > 0);

  const distinctLinkers = linkerCounts.length;
  const maxSingleUse = linkerCounts.reduce((max, l) => Math.max(max, l.count), 0);
  const isMechanical = maxSingleUse >= 4 && distinctLinkers <= 2;

  let paragraphScore;
  if (paragraphs.length <= 1) paragraphScore = 4;
  else if (paragraphs.length === 2) paragraphScore = 5.5;
  else if (paragraphs.length >= 3 && paragraphs.length <= 5) paragraphScore = 7.5;
  else paragraphScore = 6.5;

  let linkerScore;
  if (distinctLinkers === 0) linkerScore = 4.5;
  else if (distinctLinkers <= 2) linkerScore = 6;
  else if (distinctLinkers <= 5) linkerScore = 7.5;
  else linkerScore = 6;

  if (isMechanical) linkerScore = Math.max(4.5, linkerScore - 1.5);

  const hasReferencing = REFERENCING_MARKERS.some((m) => lower.includes(m));
  const referencingScore = hasReferencing ? 7 : 5.5;

  const band = roundToHalfBand((paragraphScore + linkerScore + referencingScore) / 3);

  return {
    band,
    note: isMechanical
      ? "Cohesive devices are overused/repetitive — vary your linking words instead of reusing the same one."
      : distinctLinkers === 0
      ? "Consider using linking words (however, therefore, furthermore) to connect ideas."
      : !hasReferencing
      ? "Good linking words, but try referencing earlier ideas too (e.g. 'this shows', 'these factors')."
      : "Good use of paragraphing, varied linking words, and referencing."
  };
};

const scoreLexical = (text) => {
  const words = text.toLowerCase().match(/[a-z']+/g) || [];

  if (words.length === 0) {
    return { band: 4, note: "No content to assess.", repetitionRatio: 0 };
  }

  const contentWords = words.filter((w) => !STOPWORDS.has(w));
  const uniqueWords = new Set(contentWords);
  const typeTokenRatio = uniqueWords.size / words.length;
  const avgWordLength = words.reduce((sum, w) => sum + w.length, 0) / words.length;

  const freq = {};
  contentWords.forEach((w) => {
    freq[w] = (freq[w] || 0) + 1;
  });

  const freqValues = Object.values(freq);
  // Loop instead of Math.max(...array): spreading a large array can overflow the call stack
  let maxFreq = 0;
  for (const v of freqValues) if (v > maxFreq) maxFreq = v;

  const repetitionRatio = contentWords.length ? maxFreq / contentWords.length : 0;
  const advancedWordRatio = words.filter((w) => w.length >= 8).length / words.length;

  let diversityScore;
  if (typeTokenRatio < 0.35) diversityScore = 4.5;
  else if (typeTokenRatio < 0.45) diversityScore = 5.5;
  else if (typeTokenRatio < 0.55) diversityScore = 6.5;
  else if (typeTokenRatio < 0.65) diversityScore = 7.5;
  else diversityScore = 8;

  let complexityScore;
  if (avgWordLength < 3.8) complexityScore = 5;
  else if (avgWordLength < 4.3) complexityScore = 6;
  else if (avgWordLength < 4.8) complexityScore = 7;
  else complexityScore = 7.5;

  let precisionScore;
  if (advancedWordRatio < 0.05) precisionScore = 5;
  else if (advancedWordRatio < 0.1) precisionScore = 6.5;
  else if (advancedWordRatio < 0.16) precisionScore = 7.5;
  else precisionScore = 8;

  if (repetitionRatio > 0.08) precisionScore = Math.max(4.5, precisionScore - 1.5);

  const band = roundToHalfBand((diversityScore + complexityScore + precisionScore) / 3);

  return {
    band,
    note:
      repetitionRatio > 0.08
        ? "Noticeable repetition of the same word(s) — try paraphrasing instead of reusing key terms."
        : typeTokenRatio < 0.45
        ? "Try varying vocabulary more — avoid repeating the same words."
        : "Reasonable vocabulary range detected.",
    repetitionRatio: Number(repetitionRatio.toFixed(3))
  };
};

// ============================================================================
// SCORE ESSAY
// ============================================================================
const scoreEssay = async (text, task, apiUrl) => {
  const allPromptsText = PROMPTS_TEXT_BY_MOCK[task.mock || 1];
  const copyRatio = Math.max(getCopyRatio(text, task.prompt), getCopyRatio(text, allPromptsText));

  if (copyRatio >= 0.5) {
    const note =
      "This response is copied from the task prompt/question rather than being original writing. Copied or memorised answers are not creditable under real IELTS marking rules.";

    return {
      overall: 1,
      taskAchievement: { band: 1, note, offTopic: false },
      coherence: { band: 1, note },
      lexical: { band: 1, note },
      grammar: { band: 1, note, source: "n/a" },
      copied: true,
      template: null,
      plagiarism: null,
      confidence: {
        pct: 95,
        label: "High",
        reasons: ["Exact copy of the prompt — trivially confident this is not creditable"]
      }
    };
  }

  const taBase = scoreTaskAchievement(text, task);
  const ccBase = scoreCoherence(text);
  const lrBase = scoreLexical(text);
  const collocation = scoreCollocations(text);
  const ideaDev = scoreIdeaDevelopment(text);
  const synonymVariety = scoreSynonymVariety(text);
  const grammarRange = scoreGrammarRange(text);
  const complexSentences = scoreComplexSentenceRatio(text);
  const topicSentences = scoreTopicSentences(text);
  const paragraphUnity = scoreParagraphUnity(text);

  let exampleQuality = null;
  let positionConsistency = null;

  if (task.id === "task2") {
    exampleQuality = scoreExampleQuality(text);
    positionConsistency = scorePositionConsistency(text);
  }

  let task1Note = "";
  let task1Details = null;

  if (task.id === "task1") {
    const task1Result = scoreTask1Specific(text, task);
    task1Note = task1Result.note;
    task1Details = task1Result;
    taBase.band = roundToHalfBand(clampBand(taBase.band + task1Result.adjustment));
  }

  const [ltMatches, reference, templateResult, plagiarismResult] = await Promise.all([
    checkGrammarWithLanguageTool(text),
    fetchReferenceCoverage(text, task, apiUrl),
    checkTemplateSimilarity(text, apiUrl),
    checkEmbeddingPlagiarism(text, apiUrl)
  ]);

  const graFallback =
    ltMatches !== null ? scoreGrammarFromLanguageTool(ltMatches, text) : scoreGrammarHeuristic(text);

  let taBand = taBase.band * 0.5 + ideaDev.band * 0.3;
  let taWeightUsed = 0.8;
  const extraNotes = [];

  if (task.id === "task2") {
    taBand += exampleQuality.band * 0.1 + positionConsistency.band * 0.1;
    taWeightUsed = 1.0;
    extraNotes.push(exampleQuality.note, positionConsistency.note);
  }

  taBand = taBand / taWeightUsed;

  // When the reference check ran, its content coverage (expected facts,
  // question parts, arguments, position) carries 40% of Task Achievement.
  if (reference) {
    taBand = taBand * 0.6 + coverageToBand(reference.contentCoverage) * 0.4;
  }

  const ta = {
    band: roundToHalfBand(clampBand(taBand)),
    note: `${taBase.note}${task1Note ? " " + task1Note : ""}`,
    offTopic: !!reference?.offTopic,
    ideaDevelopment: ideaDev,
    exampleQuality,
    positionConsistency,
    task1Specific: task1Note,
    task1Details,
    reference,
    supportingNotes: extraNotes
  };

  const cc = {
    band: roundToHalfBand(
      clampBand(ccBase.band * 0.6 + topicSentences.band * 0.2 + paragraphUnity.band * 0.2)
    ),
    note: ccBase.note,
    topicSentences,
    paragraphUnity
  };

  const lr = {
    band: roundToHalfBand(
      clampBand(lrBase.band * 0.5 + collocation.band * 0.3 + synonymVariety.band * 0.2)
    ),
    note: lrBase.note,
    collocation,
    synonymVariety,
    repetitionRatio: lrBase.repetitionRatio
  };

  const gra = {
    band: roundToHalfBand(
      clampBand(graFallback.band * 0.6 + grammarRange.band * 0.25 + complexSentences.band * 0.15)
    ),
    note: `${graFallback.note} ${grammarRange.note}`,
    source: graFallback.source,
    grammarRange,
    complexSentences
  };

  if (ta.offTopic) {
    ta.band = Math.min(ta.band, 3.5);
    ta.note = `This response does not appear to address the task prompt. ${ta.note}`;
  }

  if (copyRatio >= 0.2) {
    ta.band = Math.max(1, roundToHalfBand(ta.band - 3));
    ta.note =
      "Significant portions of this response are copied from the prompt — this substantially limits Task Achievement even though some original writing is present.";
  }

  if (templateResult?.isTemplate) {
    ta.band = Math.max(1, roundToHalfBand(ta.band - 2.5));
    ta.note = `This response closely matches a known template/model essay pattern (similarity ${(
      templateResult.similarity * 100
    ).toFixed(0)}%). ${ta.note}`;
  }

  if (plagiarismResult?.isPlagiarized) {
    ta.band = Math.max(1, roundToHalfBand(ta.band - 2.5));
    ta.note = `This response closely matches other existing text found via similarity search (similarity ${(
      plagiarismResult.similarity * 100
    ).toFixed(0)}%). ${ta.note}`;
  }

  const overall = roundOverallBand((ta.band + cc.band + lr.band + gra.band) / 4);

  const conf = computeConfidence({
    reference,
    taBase,
    grammarSource: gra.source,
    wordCount: getWordCount(text),
    minWords: task.minWords,
    copied: false,
    template: templateResult,
    plagiarism: plagiarismResult
  });

  return {
    overall,
    taskAchievement: ta,
    coherence: cc,
    lexical: lr,
    grammar: gra,
    copied: false,
    template: templateResult,
    plagiarism: plagiarismResult,
    confidence: conf
  };
};

// ============================================================================
// SCROLL HELPER
// Resets the window AND the common scrolling roots. Some layouts scroll
// document.documentElement / document.body / a wrapper instead of the window.
// ============================================================================
const scrollPageToTop = (behavior = "auto") => {
  try {
    window.scrollTo({ top: 0, left: 0, behavior });
  } catch (e) {
    window.scrollTo(0, 0);
  }
  if (document.documentElement) document.documentElement.scrollTop = 0;
  if (document.body) document.body.scrollTop = 0;
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================
// ============================================================================
// DRAFT AUTOSAVE (localStorage)
// Stores the essays, the active task and the exam DEADLINE (not "time left"),
// so a refresh, crash or throttled background tab can never reset or stretch
// the 60-minute clock. Cleared once the result is saved to the server.
// NOTE: the key is per browser. If several students share one browser, append
// the user id to the key (e.g. `${draftKey(mock)}:${user.id}`).
// ============================================================================
// One draft per mock, so a Mock 1 draft never loads into another mock.
const draftKey = (mock) => (mock === 1 ? "ielts-writing-draft-v1" : `ielts-writing-draft-v1:mock${mock}`);
const EXAM_SECONDS = 60 * 60;
const DRAFT_GRACE_MS = 5 * 60 * 1000; // draft older than this past its deadline is discarded

const loadDraft = (key) => {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const d = JSON.parse(raw);
    if (!d || typeof d.deadline !== "number" || !d.responses) return null;
    if (Date.now() - d.deadline > DRAFT_GRACE_MS) {
      window.localStorage.removeItem(key);
      return null;
    }
    return {
      responses: {
        task1: typeof d.responses.task1 === "string" ? d.responses.task1 : "",
        task2: typeof d.responses.task2 === "string" ? d.responses.task2 : ""
      },
      activeTask: d.activeTask === 1 ? 1 : 0,
      deadline: d.deadline
    };
  } catch (e) {
    return null;
  }
};

const saveDraft = (key, draft) => {
  try {
    window.localStorage.setItem(key, JSON.stringify({ ...draft, savedAt: Date.now() }));
    return true;
  } catch (e) {
    return false; // storage full / blocked (private mode) — exam still works
  }
};

const clearDraft = (key) => {
  try {
    window.localStorage.removeItem(key);
  } catch (e) {
    /* ignore */
  }
};

export default function IeltsWriting({ mock = 1 }) {
  const navigate = useNavigate();
  const WRITING_DATA = WRITING_DATA_BY_MOCK[mock] || IELTS_DATA_WRITING;
  const API_URL = getApiBaseUrl();

  const [initialDraft] = useState(() => loadDraft(draftKey(mock)));
  const [activeTask, setActiveTask] = useState(initialDraft?.activeTask ?? 0);
  const [responses, setResponses] = useState(initialDraft?.responses ?? { task1: "", task2: "" });
  const deadlineRef = useRef(initialDraft?.deadline ?? Date.now() + EXAM_SECONDS * 1000);
  const [timeLeft, setTimeLeft] = useState(() =>
    Math.max(0, Math.ceil((deadlineRef.current - Date.now()) / 1000))
  );
  const [draftRestored, setDraftRestored] = useState(
    !!initialDraft && (initialDraft.responses.task1.trim() !== "" || initialDraft.responses.task2.trim() !== "")
  );
  const [draftSavedAt, setDraftSavedAt] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [writingScore, setWritingScore] = useState(null);
  const [breakdown, setBreakdown] = useState(null);
  const [copyWarnings, setCopyWarnings] = useState({ task1: false, task2: false });
  const [templateWarnings, setTemplateWarnings] = useState({ task1: null, task2: null });
  const [plagiarismWarnings, setPlagiarismWarnings] = useState({ task1: null, task2: null });
  const [offTopicWarnings, setOffTopicWarnings] = useState({ task1: false, task2: false });
  const [taskDetails, setTaskDetails] = useState(null);
  const [confidence, setConfidence] = useState(null);
  // idle | scoring | saving | saved | error | unauthorized
  const [saveStatus, setSaveStatus] = useState("idle");
  // Reason returned by the server when saving fails (e.g. validation message on a 400)
  const [saveErrorMessage, setSaveErrorMessage] = useState("");
  const [wasAutoSubmitted, setWasAutoSubmitted] = useState(false);
  const [grammarSource, setGrammarSource] = useState(null);
  // Which tasks got the reference content check (true also for copied answers, where it doesn't apply)
  const [referenceByTask, setReferenceByTask] = useState({ task1: true, task2: true });
  const [calibrationApplied, setCalibrationApplied] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [showConfidenceDetails, setShowConfidenceDetails] = useState(false);

  const latestRef = useRef({ activeTask, responses, submitted });
  const scoringStartedRef = useRef(false); // prevents double scoring (manual + auto)
  const pendingSaveRef = useRef(null); // payload kept so "Retry Save" does NOT re-score
  const finalizeRef = useRef(null); // always points at the newest finalizeAndSave
  const containerRef = useRef(null); // the page wrapper (used to reset inner scrolling too)

  useEffect(() => {
    latestRef.current = { activeTask, responses, submitted };
  }, [activeTask, responses, submitted]);

  // ==========================================================================
  // SCROLL TO TOP
  // 1) On first open: useLayoutEffect runs before the browser paints, so the
  //    page never flashes in the middle. Also stops the browser from restoring
  //    the previous scroll position (history.scrollRestoration = "manual").
  // ==========================================================================
  useLayoutEffect(() => {
    let previousRestoration;
    if ("scrollRestoration" in window.history) {
      previousRestoration = window.history.scrollRestoration;
      window.history.scrollRestoration = "manual";
    }

    scrollPageToTop("auto");
    if (containerRef.current) containerRef.current.scrollTop = 0;

    // Some content (image, fonts) can shift layout right after mount, so reset
    // once more on the next frame.
    const raf = requestAnimationFrame(() => {
      scrollPageToTop("auto");
      if (containerRef.current) containerRef.current.scrollTop = 0;
    });

    return () => {
      cancelAnimationFrame(raf);
      if (previousRestoration !== undefined && "scrollRestoration" in window.history) {
        window.history.scrollRestoration = previousRestoration;
      }
    };
  }, []);

  // 2) When the result card appears (layout switches to height:auto), go to top
  //    so the score is visible instead of appearing off-screen.
  useEffect(() => {
    if (submitted) {
      scrollPageToTop("smooth");
      if (containerRef.current) containerRef.current.scrollTop = 0;
    }
  }, [submitted]);

  // 3) When switching between Task 1 and Task 2, start from the top as well.
  useEffect(() => {
    scrollPageToTop("auto");
  }, [activeTask]);

  // Countdown. Stops once the test is submitted.
  useEffect(() => {
    if (submitted) return undefined;
    // Derived from a fixed deadline, so refreshes and background-tab throttling
    // cannot make the clock drift or restart.
    const timer = setInterval(() => {
      setTimeLeft(Math.max(0, Math.ceil((deadlineRef.current - Date.now()) / 1000)));
    }, 1000);
    return () => clearInterval(timer);
  }, [submitted]);

  // Auto-submit when time hits 0 (done in an effect, NOT inside a state updater)
  useEffect(() => {
    if (timeLeft === 0 && !latestRef.current.submitted && !scoringStartedRef.current) {
      setWasAutoSubmitted(true);
      if (finalizeRef.current) {
        finalizeRef.current(latestRef.current.responses, { force: true });
      }
    }
  }, [timeLeft]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
  };

  const currentTask = WRITING_DATA.tasks[activeTask];
  const wordCount = getWordCount(responses[currentTask.id] || "");
  const task1Done = getWordCount(responses.task1) >= WRITING_DATA.tasks[0].minWords;
  const task2Done = getWordCount(responses.task2) >= WRITING_DATA.tasks[1].minWords;

  // ==========================================================================
  // AUTOSAVE: debounced write to localStorage while the exam is in progress
  // ==========================================================================
  useEffect(() => {
    if (submitted) return undefined;
    const t = setTimeout(() => {
      const ok = saveDraft(draftKey(mock), { responses, activeTask, deadline: deadlineRef.current });
      if (ok) setDraftSavedAt(new Date());
    }, 600);
    return () => clearTimeout(t);
  }, [responses, activeTask, submitted]);

  // Warn before closing/refreshing with unsaved work, and flush the draft one last time
  useEffect(() => {
    const handler = (e) => {
      const { responses: r, activeTask: a, submitted: s } = latestRef.current;
      if (!s) saveDraft(draftKey(mock), { responses: r, activeTask: a, deadline: deadlineRef.current });
      const hasWork = getWordCount(r.task1) + getWordCount(r.task2) > 0;
      const unsavedResult = s && !!pendingSaveRef.current;
      if ((!s && hasWork) || unsavedResult) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, []);

  // "Draft restored" banner disappears on its own
  useEffect(() => {
    if (!draftRestored) return undefined;
    const t = setTimeout(() => setDraftRestored(false), 7000);
    return () => clearTimeout(t);
  }, [draftRestored]);

  // Exam mode: adds a class on <body> while the test is in progress (removed on
  // submit or when leaving the page). Your navbar/global CSS can use it to hide
  // the distracting links, e.g.  body.exam-mode .nav-links { display: none; }
  useEffect(() => {
    if (submitted) return undefined;
    document.body.classList.add("exam-mode");
    return () => document.body.classList.remove("exam-mode");
  }, [submitted]);

  // ==========================================================================
  // SAVE (separate from scoring so a failed save can be retried safely)
  // ==========================================================================
  const saveToServer = async (payload) => {
    setSaveStatus("saving");
    setSaveErrorMessage("");
    try {
      const res = await apiFetch(API_URL, "/tests/save", {
        method: "POST",
        body: JSON.stringify(payload)
      });

      if (res.status === 401) {
        setSaveStatus("unauthorized");
        return;
      }

      if (!res.ok) {
        // Read the server's explanation so we (and the user) can see WHY it was rejected
        let detail = "";
        try {
          const body = await res.json();
          detail = body?.message || body?.error || JSON.stringify(body);
        } catch (parseErr) {
          /* response had no JSON body */
        }
        console.warn(`Save failed (${res.status}): ${detail || "no details returned"}`);
        setSaveErrorMessage(detail || `Server responded with status ${res.status}`);
        setSaveStatus("error");
        return;
      }

      pendingSaveRef.current = null;
      clearDraft(draftKey(mock));
      setSaveStatus("saved");
    } catch (err) {
      console.error("Save failed", err);
      setSaveErrorMessage("Network error — could not reach the server.");
      setSaveStatus("error");
    }
  };

  // ==========================================================================
  // FINALIZE
  // ==========================================================================
  const finalizeAndSave = async (responsesSnapshot, { force = false } = {}) => {
    if (latestRef.current.submitted || scoringStartedRef.current) return;

    const words1 = getWordCount(responsesSnapshot.task1);
    const words2 = getWordCount(responsesSnapshot.task2);

    if (!force && (words1 < 20 || words2 < 20)) {
      alert("Please complete both tasks before submitting the test.");
      return;
    }

    scoringStartedRef.current = true;
    setSaveStatus("scoring");

    let result1;
    let result2;
    let calibration;

    try {
      // Sequential on purpose: grading both essays at the same instant is what
      // triggers rate limits (HTTP 429) on LanguageTool.
      const calibrationPromise = fetchScoringCalibration(API_URL); // cheap, never rejects
      result1 = await scoreEssay(responsesSnapshot.task1 || "", WRITING_DATA.tasks[0], API_URL);
      result2 = await scoreEssay(responsesSnapshot.task2 || "", WRITING_DATA.tasks[1], API_URL);
      calibration = await calibrationPromise;
    } catch (err) {
      console.error("Scoring failed unexpectedly", err);
      scoringStartedRef.current = false; // allow the user to try again
      setSaveStatus("error");
      alert("Something went wrong while scoring your writing. Please try submitting again.");
      return;
    }

    setCopyWarnings({ task1: !!result1.copied, task2: !!result2.copied });
    setTemplateWarnings({ task1: result1.template, task2: result2.template });
    setPlagiarismWarnings({ task1: result1.plagiarism, task2: result2.plagiarism });
    setOffTopicWarnings({
      task1: !!result1.taskAchievement?.offTopic,
      task2: !!result2.taskAchievement?.offTopic
    });
    setTaskDetails({ task1: result1, task2: result2 });

    const reference1 = result1.taskAchievement?.reference || null;
    const reference2 = result2.taskAchievement?.reference || null;
    setReferenceByTask({
      task1: !!reference1 || !!result1.copied,
      task2: !!reference2 || !!result2.copied
    });

    const sources = [result1.grammar.source, result2.grammar.source].filter((s) => s && s !== "n/a");
    const isFallback = (s) => s === "heuristic";

    if (sources.length === 0) setGrammarSource(null);
    else if (sources.every(isFallback)) setGrammarSource("heuristic");
    else if (sources.every((s) => !isFallback(s))) setGrammarSource("languagetool");
    else setGrammarSource("mixed");

    const combinedConfidencePct = Math.round(
      (result1.confidence.pct + 2 * result2.confidence.pct) / 3
    );
    const combinedConfidenceLabel =
      combinedConfidencePct >= 80 ? "High" : combinedConfidencePct >= 50 ? "Medium" : "Low";

    setConfidence({
      pct: combinedConfidencePct,
      label: combinedConfidenceLabel,
      task1: result1.confidence,
      task2: result2.confidence
    });

    // Keep the Task 2-weighted criterion averages unrounded so the overall
    // band is rounded only once — rounding each criterion first and then
    // the average could drift the result by half a band.
    let rawCombined = {
      taskAchievement: (result1.taskAchievement.band + 2 * result2.taskAchievement.band) / 3,
      coherence: (result1.coherence.band + 2 * result2.coherence.band) / 3,
      lexical: (result1.lexical.band + 2 * result2.lexical.band) / 3,
      grammar: (result1.grammar.band + 2 * result2.grammar.band) / 3
    };

    if (calibration) {
      const offset = (v, o) => (typeof o === "number" && o ? clampBand(v + o) : v);
      rawCombined = {
        taskAchievement: offset(rawCombined.taskAchievement, calibration.taOffset),
        coherence: offset(rawCombined.coherence, calibration.ccOffset),
        lexical: offset(rawCombined.lexical, calibration.lrOffset),
        grammar: offset(rawCombined.grammar, calibration.graOffset)
      };
      setCalibrationApplied(true);
    } else {
      setCalibrationApplied(false);
    }

    // Per-criterion bands shown to the user are still whole/half bands.
    const combined = {
      taskAchievement: roundToHalfBand(rawCombined.taskAchievement),
      coherence: roundToHalfBand(rawCombined.coherence),
      lexical: roundToHalfBand(rawCombined.lexical),
      grammar: roundToHalfBand(rawCombined.grammar)
    };

    const finalScore = roundOverallBand(
      (rawCombined.taskAchievement + rawCombined.coherence + rawCombined.lexical + rawCombined.grammar) / 4
    );

    setWritingScore(finalScore);
    setBreakdown(combined);
    setSubmitted(true);

    const payload = {
      testName: mock === 1 ? "IELTS Writing" : `IELTS Writing Mock ${mock}`,
      result: {
        score: finalScore,
        task1Band: result1.overall,
        task2Band: result2.overall,
        breakdown: combined,
        confidence: combinedConfidencePct,
        copied: { task1: !!result1.copied, task2: !!result2.copied },
        template: {
          task1: result1.template?.isTemplate || false,
          task2: result2.template?.isTemplate || false
        },
        plagiarism: {
          task1: result1.plagiarism?.isPlagiarized || false,
          task2: result2.plagiarism?.isPlagiarized || false
        },
        offTopic: {
          task1: !!result1.taskAchievement?.offTopic,
          task2: !!result2.taskAchievement?.offTopic
        },
        essayTask1: responsesSnapshot.task1,
        essayTask2: responsesSnapshot.task2,
        wordCountTask1: words1,
        wordCountTask2: words2,
        // 0-1 fractions (the server rejects numbers above the band ceiling); null if the check was unavailable
        referenceCoverage: {
          task1: reference1 ? reference1.contentCoverage : null,
          task2: reference2 ? reference2.contentCoverage : null
        },
        calibrationApplied: !!calibration
      }
    };

    pendingSaveRef.current = payload;
    await saveToServer(payload);
  };

  // keep the ref pointing at the newest version (used by the auto-submit effect)
  finalizeRef.current = finalizeAndSave;

  const handleManualSubmit = () => {
    if (submitted || scoringStartedRef.current) return;

    if (activeTask === 0) {
      setActiveTask(1);
      return;
    }

    const w1 = getWordCount(responses.task1);
    const w2 = getWordCount(responses.task2);
    const t1Min = WRITING_DATA.tasks[0].minWords;
    const t2Min = WRITING_DATA.tasks[1].minWords;
    const short = [];
    if (w1 < t1Min) short.push(`Task 1: ${w1}/${t1Min} words`);
    if (w2 < t2Min) short.push(`Task 2: ${w2}/${t2Min} words`);

    const message =
      (short.length ? `Below the recommended word count:\n• ${short.join("\n• ")}\n\n` : "") +
      "Submit your writing test now? You cannot change your answers after submitting.";

    if (!window.confirm(message)) return;

    finalizeAndSave(responses, { force: false });
  };

  // Retry ONLY the save, using the already computed result. No re-scoring.
  const retrySave = () => {
    if (pendingSaveRef.current) {
      saveToServer(pendingSaveRef.current);
    }
  };

  const isScoring = saveStatus === "scoring";

  return (
    <div ref={containerRef} className={`ielts-container ${submitted ? "is-submitted" : ""}`}>
      {isScoring && !submitted && (
        <div
          style={{
            maxWidth: "650px",
            margin: "0 auto 14px auto",
            background: "#f0fdf4",
            border: "1px solid #bbf7d0",
            borderRadius: "16px",
            padding: "14px 24px",
            textAlign: "center",
            color: "#166534",
            fontWeight: 600,
            flexShrink: 0
          }}
        >
          Checking your writing — content against the reference points, logical flow, vocabulary, and grammar…
          <div style={{ fontWeight: 400, fontSize: "0.85rem", marginTop: "6px" }}>
            This usually takes a few seconds. Please keep this page open.
          </div>
        </div>
      )}

      {submitted && (
        <div
          style={{
            maxWidth: "650px",
            margin: "0 auto 20px auto",
            background: "#ffffff",
            border: "1px solid #bbf7d0",
            borderRadius: "16px",
            padding: "24px",
            textAlign: "center",
            boxShadow: "0 10px 30px rgba(0,0,0,0.08)"
          }}
        >
          <div style={{ fontSize: "40px", marginBottom: "6px" }}>
            {copyWarnings.task1 || copyWarnings.task2 ? "🚫" : "🎉"}
          </div>

          <h2 style={{ fontSize: "36px", color: "#16a34a", marginBottom: "4px", fontWeight: "bold" }}>
            Band {writingScore}/9
          </h2>

          <p style={{ color: "#6b7280", fontSize: "0.85rem", marginBottom: "20px" }}>
            Task 2 counts twice as much as Task 1 toward your final band, as in the real IELTS exam.
          </p>

          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: "8px",
              flexWrap: "wrap",
              marginBottom: "10px"
            }}
          >
            <Badge
              active={referenceByTask.task1 && referenceByTask.task2}
              activeText="Content checked against reference points"
              inactiveText="Reference content check unavailable"
            />
            <Badge
              active={calibrationApplied}
              activeText="Calibrated against human scores"
              inactiveText="No human-score calibration available"
            />
          </div>

          {confidence && (
            <div style={{ marginBottom: "12px" }}>
              <button
                onClick={() => setShowConfidenceDetails(!showConfidenceDetails)}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                  fontSize: "0.75rem",
                  fontWeight: 700,
                  padding: "5px 12px",
                  border: "1px solid #e5e7eb",
                  background: "#f9fafb",
                  color: "#374151",
                  cursor: "pointer"
                }}
              >
                <FiInfo />
                Confidence: {confidence.label} ({confidence.pct}%)
              </button>

              {showConfidenceDetails && (
                <div
                  style={{
                    textAlign: "left",
                    maxWidth: "450px",
                    margin: "10px auto 0 auto",
                    fontSize: "0.75rem",
                    color: "#6b7280",
                    background: "#f9fafb",
                    border: "1px solid #e5e7eb",
                    borderRadius: "8px",
                    padding: "10px 14px"
                  }}
                >
                  This reflects how much to trust this specific auto-score (not your writing ability). Task 1:{" "}
                  {confidence.task1?.reasons?.join("; ")}. Task 2: {confidence.task2?.reasons?.join("; ")}.
                </div>
              )}
            </div>
          )}

          {(!referenceByTask.task1 || !referenceByTask.task2) && (
            <Notice
              text={`The reference content check was unavailable for ${
                !referenceByTask.task1 && !referenceByTask.task2
                  ? "both tasks"
                  : !referenceByTask.task1
                  ? "Task 1"
                  : "Task 2"
              }, so Task Achievement was estimated from general rule-based checks only.`}
            />
          )}

          {grammarSource === "heuristic" && (
            <Notice text="Automated grammar checking was unavailable, so the Grammatical Range & Accuracy band is a rough estimate rather than a real error check." />
          )}

          {grammarSource === "mixed" && (
            <Notice text="Automated grammar checking succeeded for one task but not the other, so the Grammatical Range & Accuracy band is partly a rough estimate." />
          )}

          {(offTopicWarnings.task1 || offTopicWarnings.task2) && (
            <WarningBox>
              {offTopicWarnings.task1 && offTopicWarnings.task2
                ? "Both responses appear to be off-topic relative to their task prompts."
                : offTopicWarnings.task1
                ? "Your Task 1 response appears to be off-topic relative to the prompt."
                : "Your Task 2 response appears to be off-topic relative to the prompt."}{" "}
              This substantially limits the Task Achievement band regardless of writing quality.
            </WarningBox>
          )}

          {(templateWarnings.task1?.isTemplate || templateWarnings.task2?.isTemplate) && (
            <Notice
              text={
                (templateWarnings.task1?.isTemplate && templateWarnings.task2?.isTemplate
                  ? "Both responses closely match known template/model essays"
                  : templateWarnings.task1?.isTemplate
                  ? "Your Task 1 response closely matches a known template/model essay"
                  : "Your Task 2 response closely matches a known template/model essay") +
                " rather than appearing to be original writing for this specific prompt. This limits your Task Achievement band."
              }
            />
          )}

          {(plagiarismWarnings.task1?.isPlagiarized || plagiarismWarnings.task2?.isPlagiarized) && (
            <WarningBox>
              {plagiarismWarnings.task1?.isPlagiarized && plagiarismWarnings.task2?.isPlagiarized
                ? "Both responses closely match other existing text found via similarity search."
                : plagiarismWarnings.task1?.isPlagiarized
                ? "Your Task 1 response closely matches other existing text found via similarity search."
                : "Your Task 2 response closely matches other existing text found via similarity search."}{" "}
              This substantially limits Task Achievement.
            </WarningBox>
          )}

          {(copyWarnings.task1 || copyWarnings.task2) && (
            <WarningBox>
              {copyWarnings.task1 && copyWarnings.task2
                ? "Both responses appear to be copied from the task prompts rather than original writing."
                : copyWarnings.task1
                ? "Your Task 1 response appears to be copied from the task prompt rather than original writing."
                : "Your Task 2 response appears to be copied from the task prompt rather than original writing."}{" "}
              Copied or memorised text isn't creditable under real IELTS rules, so this score reflects that.
            </WarningBox>
          )}

          {wasAutoSubmitted && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                justifyContent: "center",
                background: "#fff7ed",
                border: "1px solid #fed7aa",
                color: "#9a3412",
                borderRadius: "8px",
                padding: "10px 14px",
                fontSize: "0.85rem",
                marginBottom: "12px"
              }}
            >
              <FiAlertTriangle />
              Time ran out — this was auto-submitted with whatever you had written.
            </div>
          )}

          {taskDetails && (
            <div style={{ margin: "0 auto 20px auto", maxWidth: "480px", textAlign: "left" }}>
              <button
                onClick={() => setShowDetails(!showDetails)}
                style={{
                  background: "none",
                  border: "none",
                  color: "#16a34a",
                  fontWeight: 600,
                  fontSize: "0.85rem",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  padding: "6px 0"
                }}
              >
                <FiInfo />
                {showDetails ? "Hide" : "Show"} detailed feedback
              </button>

              {showDetails && (
                <div style={{ display: "flex", flexDirection: "column", gap: "16px", marginTop: "10px" }}>
                  <TaskFeedback title="Task 1 Feedback" result={taskDetails.task1} />
                  <TaskFeedback title="Task 2 Feedback" result={taskDetails.task2} />
                </div>
              )}
            </div>
          )}

          {/* Scoring-method details (AI/calibration status, confidence, fallbacks)
              live behind one toggle so the band and criteria stay front and centre. */}
          <div style={{ margin: "0 auto 20px auto", maxWidth: "480px", textAlign: "left" }}>
            <button
              onClick={() => setShowConfidenceDetails(!showConfidenceDetails)}
              style={{
                background: "none",
                border: "none",
                color: "#6b7280",
                fontWeight: 600,
                fontSize: "0.8rem",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                padding: "6px 0"
              }}
            >
              <FiInfo />
              {showConfidenceDetails ? "Hide" : "About"} this score
              {confidence && ` · Confidence: ${confidence.label} (${confidence.pct}%)`}
            </button>

            {showConfidenceDetails && (
              <div
                style={{
                  marginTop: "8px",
                  fontSize: "0.78rem",
                  color: "#6b7280",
                  background: "#f9fafb",
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                  padding: "12px 14px"
                }}
              >
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-start",
                  gap: "8px",
                  flexWrap: "wrap",
                  marginBottom: "10px"
                }}
              >
                <Badge
                  active={referenceByTask.task1 && referenceByTask.task2}
                  activeText="Content checked against reference points"
                  inactiveText="Reference content check unavailable"
                />
                <Badge
                  active={calibrationApplied}
                  activeText="Calibrated against human scores"
                  inactiveText="No human-score calibration available"
                />
              </div>
                {confidence && (
                  <p style={{ margin: "0 0 8px" }}>
                    Confidence reflects how much to trust this specific auto-score (not your writing ability). Task 1:{" "}
                    {confidence.task1?.reasons?.join("; ")}. Task 2: {confidence.task2?.reasons?.join("; ")}.
                  </p>
                )}
              <p style={{ margin: "0 0 8px" }}>
                All scoring runs on fixed rules: your answer is compared with saved reference points for
                this question (expected facts, question parts, accepted arguments) plus general checks of
                structure, vocabulary and grammar. It is a practice estimate, not an official IELTS band, and
                it cannot fully judge how well ideas are developed.
              </p>
    
              {grammarSource === "heuristic" && (
                <Notice text="Automated grammar checking was unavailable, so the Grammatical Range & Accuracy band is a rough estimate rather than a real error check." />
              )}
    
              {grammarSource === "mixed" && (
                <Notice text="Automated grammar checking succeeded for one task but not the other, so the Grammatical Range & Accuracy band is partly a rough estimate." />
              )}
              </div>
            )}
          </div>

          {saveStatus === "saving" && (
            <p style={{ color: "#6b7280", marginBottom: "14px" }}>Saving your result…</p>
          )}

          {saveStatus === "saved" && (
            <p style={{ color: "#374151", marginBottom: "14px" }}>
              Your writing test has been successfully saved.
            </p>
          )}

          {saveStatus === "unauthorized" && (
            <div
              style={{
                background: "#fef2f2",
                border: "1px solid #fecaca",
                color: "#dc2626",
                borderRadius: "8px",
                padding: "12px 16px",
                fontSize: "0.85rem",
                marginBottom: "14px",
                textAlign: "left"
              }}
            >
              ⚠️ Your score was calculated, but your login session has expired so it could not be saved.
              Open the login page in a new tab, sign in again, then come back here and press Retry Save.
              Your score is kept on this page.
              <div style={{ marginTop: "10px", display: "flex", gap: "10px" }}>
                <button
                  onClick={() => window.open("/login", "_blank")}
                  style={{
                    background: "#ffffff",
                    color: "#dc2626",
                    border: "1px solid #dc2626",
                    padding: "6px 14px",
                    borderRadius: "6px",
                    fontWeight: 700,
                    cursor: "pointer",
                    fontSize: "0.8rem"
                  }}
                >
                  Open login
                </button>
                <button
                  onClick={retrySave}
                  style={{
                    background: "#dc2626",
                    color: "white",
                    border: "none",
                    padding: "6px 14px",
                    borderRadius: "6px",
                    fontWeight: 700,
                    cursor: "pointer",
                    fontSize: "0.8rem"
                  }}
                >
                  Retry Save
                </button>
              </div>
            </div>
          )}

          {saveStatus === "error" && (
            <div
              style={{
                background: "#fef2f2",
                border: "1px solid #fecaca",
                color: "#dc2626",
                borderRadius: "8px",
                padding: "12px 16px",
                fontSize: "0.85rem",
                marginBottom: "14px",
                textAlign: "left"
              }}
            >
              ⚠️ We calculated your score, but saving it to your profile failed (network or server error).
              {saveErrorMessage && (
                <div style={{ marginTop: "6px", fontSize: "0.8rem" }}>Reason: {saveErrorMessage}</div>
              )}
              <div style={{ marginTop: "10px" }}>
                <button
                  onClick={retrySave}
                  style={{
                    background: "#dc2626",
                    color: "white",
                    border: "none",
                    padding: "6px 14px",
                    borderRadius: "6px",
                    fontWeight: 700,
                    cursor: "pointer",
                    fontSize: "0.8rem"
                  }}
                >
                  Retry Save
                </button>
              </div>
            </div>
          )}

          {breakdown && (
            <div style={{ textAlign: "left", margin: "0 auto 10px auto", maxWidth: "450px" }}>
              <BreakdownRow label="Task Achievement" value={breakdown.taskAchievement} />
              <BreakdownRow label="Coherence & Cohesion" value={breakdown.coherence} />
              <BreakdownRow label="Lexical Resource" value={breakdown.lexical} />
              <BreakdownRow label="Grammatical Range & Accuracy" value={breakdown.grammar} />
            </div>
          )}

          {taskDetails && (
            <div style={{ margin: "0 auto 14px auto", maxWidth: "450px", textAlign: "left" }}>
              <button
                onClick={() => setShowDetails(!showDetails)}
                style={{
                  background: "none",
                  border: "none",
                  color: "#16a34a",
                  fontWeight: 600,
                  fontSize: "0.85rem",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  padding: "6px 0"
                }}
              >
                <FiInfo />
                {showDetails ? "Hide" : "Show"} detailed feedback
              </button>

              {showDetails && (
                <div style={{ display: "flex", flexDirection: "column", gap: "16px", marginTop: "10px" }}>
                  <TaskFeedback title="Task 1 Feedback" result={taskDetails.task1} />
                  <TaskFeedback title="Task 2 Feedback" result={taskDetails.task2} />
                </div>
              )}
            </div>
          )}

          <div style={{ display: "flex", justifyContent: "center", gap: "15px" }}>
            <button
              onClick={() => navigate("/profile")}
              style={{
                background: "#16a34a",
                color: "white",
                border: "none",
                padding: "10px 20px",
                borderRadius: "8px",
                fontWeight: "bold",
                cursor: "pointer"
              }}
            >
              Go to Profile
            </button>
          </div>
        </div>
      )}

      {/* ====================================================================
          LAYOUT STYLES (tightened)
          --nav-offset = height of your fixed navbar. If the page still has a
          gap above the tabs (or the tabs hide under the navbar), change only
          this one number.
          ==================================================================== */}
      <style>{`
        .ielts-container {
          --nav-offset: calc(var(--tx-nav-h, 84px) + 34px);
          height: 100vh;
          display: flex;
          flex-direction: column;
          background: #ffffff;
          padding: var(--nav-offset) 1.5rem 1rem 1.5rem;
          color: #111827;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
          box-sizing: border-box;
          overflow: hidden;
        }
        .ielts-container.is-submitted {
          height: auto;
          min-height: 100vh;
          overflow: visible;
        }
        .ielts-container .header-container {
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
          max-width: 1500px;
          width: 100%;
          margin: 0 auto 12px auto;
          border-bottom: 1px solid #d1fae5;
          padding-bottom: 10px;
          flex-shrink: 0;
        }
        .ielts-container .test-title {
          position: absolute;
          left: 50%;
          transform: translateX(-50%);
          margin: 0;
          font-size: 1.6rem;
          font-weight: 800;
          line-height: 1.2;
          color: #111827;
          white-space: nowrap;
        }
        .ielts-container .tabs {
          position: relative;
          margin-right: auto;
          display: flex;
          gap: 10px;
        }
        .ielts-container .tab-btn {
          background: #ffffff;
          border: 1px solid #bbf7d0;
          color: #15803d;
          padding: 0.5rem 1.2rem;
          border-radius: 10px;
          cursor: pointer;
          font-weight: 600;
          transition: 0.3s;
        }
        .ielts-container .tab-btn:hover { background: #dcfce7; }
        .ielts-container .tab-btn.active {
          background: #16a34a;
          border-color: #16a34a;
          color: white;
        }
        .ielts-container .timer-box {
          position: relative;
          margin-left: auto;
          display: flex;
          align-items: center;
          gap: 8px;
          font-family: monospace;
          font-size: 1.1rem;
          font-weight: 800;
          padding: 6px 14px;
          border-radius: 12px;
          border: 1px solid #bbf7d0;
          background: #f0fdf4;
          color: #166534;
        }
        .ielts-container .timer-box.timer-warning {
          background: #fee2e2;
          color: #dc2626;
          border: 1px solid #fca5a5;
        }

        /* Main area fills whatever height is left, so nothing overflows the screen */
        .ielts-container .main-container {
          flex: 1;
          min-height: 0;
          display: grid;
          grid-template-columns: ${currentTask.image ? "1fr 1fr" : "1fr"};
          grid-template-rows: minmax(0, 1fr);
          gap: 1.25rem;
          width: 100%;
          max-width: 1500px;
          margin: 0 auto;
        }
        .ielts-container .main-column {
          display: flex;
          flex-direction: column;
          gap: 1rem;
          min-height: 0;
          min-width: 0;
        }
        .ielts-container .image-container {
          background: #ffffff;
          border: 1px solid #bbf7d0;
          border-radius: 20px;
          padding: 1.25rem;
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
          display: flex;
          flex-direction: column;
          min-height: 0;
          min-width: 0;
        }
        .ielts-container .card-heading {
          margin: 0 0 0.75rem 0;
          font-size: 1.15rem;
          font-weight: 700;
          color: #15803d;
          flex-shrink: 0;
        }
        .ielts-container .image-content {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #f0fdf4;
          border-radius: 12px;
          padding: 0.75rem;
          min-height: 0;
          overflow: hidden;
        }
        .ielts-container .image-content img {
          max-width: 100%;
          max-height: 100%;
          object-fit: contain;
        }
        .ielts-container .prompt-container {
          background: #ffffff;
          border: 1px solid #bbf7d0;
          border-radius: 20px;
          padding: 1.25rem 1.5rem;
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
          flex-shrink: 0;
        }
        .ielts-container .prompt-title {
          margin: 0 0 0.5rem 0;
          font-size: 1.5rem;
          font-weight: 700;
          line-height: 1.25;
        }
        .ielts-container .prompt-text {
          margin: 0;
          color: #374151;
          line-height: 1.5;
          white-space: pre-line;
        }
        .ielts-container .response-container {
          background: #ffffff;
          border: 1px solid #bbf7d0;
          border-radius: 20px;
          padding: 1.25rem 1.5rem;
          flex: 1;
          min-height: 0;
          display: flex;
          flex-direction: column;
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
        }
        .ielts-container .word-count-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 8px;
          flex-shrink: 0;
        }
        .ielts-container .word-count-main { font-weight: 700; }
        .ielts-container .word-count-hint { font-size: 0.8rem; color: #6b7280; }
        .ielts-container .editor-area {
          background: #f9fafb;
          border: 1px solid #d1d5db;
          border-radius: 12px;
          color: #111827;
          padding: 1rem 1.2rem;
          width: 100%;
          flex: 1;
          min-height: 0;
          resize: none;
          font-family: "Courier New", monospace;
          font-size: 1.05rem;
          line-height: 1.6;
          outline: none;
          transition: 0.3s;
          box-sizing: border-box;
        }
        .ielts-container .editor-area:focus {
          border-color: #16a34a;
          box-shadow: 0 0 0 3px rgba(34, 197, 94, 0.2);
        }
        .ielts-container .editor-area::placeholder { color: #6b7280; }
        .ielts-container .primary-btn {
          background: #16a34a;
          color: white;
          font-weight: 700;
          border: none;
          border-radius: 10px;
          padding: 0.75rem 2rem;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.6rem;
          margin-top: 12px;
          width: 100%;
          flex-shrink: 0;
          transition: 0.3s;
        }
        .ielts-container .primary-btn:hover { background: #15803d; }
        .ielts-container .primary-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        /* Tablet / phone: let the page scroll normally instead of fitting one screen */
        @media (max-width: 900px) {
          .ielts-container {
            height: auto;
            min-height: 100vh;
            overflow: visible;
          }
          .ielts-container .main-container {
            grid-template-columns: 1fr;
            grid-template-rows: auto;
            flex: none;
          }
          .ielts-container .header-container {
            position: sticky;
            top: var(--tx-nav-h, 84px);
            z-index: 50;
            background: #ffffff;
            padding-top: 8px;
          }
          .ielts-container .image-content { min-height: 260px; }
          .ielts-container .editor-area { min-height: 250px; flex: none; }
        }
        @media (max-width: 600px) {
          .ielts-container {
            --nav-offset: calc(var(--tx-nav-h, 84px) + 16px);
            padding-left: 1rem;
            padding-right: 1rem;
          }
          .ielts-container .header-container {
            flex-direction: column;
            align-items: center;
            gap: 8px;
          }
          .ielts-container .test-title {
            position: static;
            transform: none;
            font-size: 1.3rem;
            text-align: center;
            white-space: normal;
          }
          .ielts-container .tabs { margin-right: 0; justify-content: center; }
          .ielts-container .timer-box {
            margin-left: 0;
            font-size: 1rem;
            padding: 6px 10px;
          }
          .ielts-container .tab-btn {
            padding: 0.4rem 0.9rem;
            font-size: 0.9rem;
          }
        }
      `}</style>

      {!submitted && draftRestored && (
        <div
          style={{
            maxWidth: "1500px",
            width: "100%",
            margin: "0 auto 10px auto",
            background: "#eff6ff",
            border: "1px solid #bfdbfe",
            color: "#1e40af",
            borderRadius: "10px",
            padding: "8px 14px",
            fontSize: "0.85rem",
            fontWeight: 600,
            flexShrink: 0,
            boxSizing: "border-box"
          }}
        >
          Your previous draft was restored and the timer continued from where it left off.
        </div>
      )}

      {!submitted && (
        <div className="header-container">
          <div className="tabs">
            <button
              onClick={() => setActiveTask(0)}
              className={`tab-btn ${activeTask === 0 ? "active" : ""}`}
            >
              Task 1{task1Done ? " ✓" : ""}
            </button>

            <button
              disabled={getWordCount(responses.task1) < 20}
              style={{
                opacity: getWordCount(responses.task1) < 20 ? 0.5 : 1,
                cursor: getWordCount(responses.task1) < 20 ? "not-allowed" : "pointer"
              }}
              onClick={() => {
                if (getWordCount(responses.task1) < 20) {
                  alert("Please complete Task 1 before moving to Task 2.");
                  return;
                }
                setActiveTask(1);
              }}
              className={`tab-btn ${activeTask === 1 ? "active" : ""}`}
            >
              Task 2{task2Done ? " ✓" : ""}
            </button>
          </div>

          <h1 className="test-title">{mock === 1 ? "IELTS Academic Writing" : `IELTS Academic Writing – Mock ${mock}`}</h1>

          <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
            <FullscreenButton />
            <div className={`timer-box ${timeLeft < 300 ? "timer-warning" : ""}`}>
              <FiClock />
              Time Left: {formatTime(timeLeft)}
            </div>
          </div>
        </div>
      )}

      {!submitted && (
        <div className="main-container">
          {currentTask.image && (
            <div className="image-container">
              <h3 className="card-heading">Visual Information</h3>
              <div className="image-content">
                <img src={currentTask.image} alt="Task Visual" />
              </div>
            </div>
          )}

          <div className="main-column">
            <div className="prompt-container">
              <h2 className="prompt-title">{currentTask.title}</h2>
              <div className="prompt-text">{currentTask.prompt}</div>
            </div>

            <div className="response-container">
              <div className="word-count-row">
                <span
                  className="word-count-main"
                  style={{ color: wordCount >= currentTask.minWords ? "#16a34a" : "#374151" }}
                >
                  Words: {wordCount} / {currentTask.minWords}
                </span>

                <span className="word-count-hint">
                  {activeTask === 0 && timeLeft < 40 * 60 && timeLeft > 0 ? (
                    <span style={{ color: "#b45309", fontWeight: 600, marginRight: "10px" }}>
                      ⏱ 20 min used — aim to move on to Task 2
                    </span>
                  ) : wordCount < currentTask.minWords ? (
                    <span style={{ marginRight: "10px" }}>
                      Recommended minimum: {currentTask.minWords} words
                    </span>
                  ) : null}
                  {draftSavedAt && (
                    <span style={{ color: "#16a34a" }}>
                      ✓ Draft autosaved {draftSavedAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                    </span>
                  )}
                </span>
              </div>

              <textarea
                className="editor-area"
                placeholder={`Type your ${activeTask === 0 ? "report" : "essay"} here...`}
                value={responses[currentTask.id] || ""}
                disabled={isScoring}
                onChange={(e) =>
                  setResponses({ ...responses, [currentTask.id]: e.target.value })
                }
              />

              <button
                onClick={handleManualSubmit}
                className="primary-btn"
                disabled={wordCount < 5 || submitted || isScoring}
              >
                {isScoring ? "Scoring…" : activeTask === 0 ? "Save & Continue to Task 2 →" : "Submit Writing Test"}
                <FiSave />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// UI COMPONENTS
// ============================================================================
function BreakdownRow({ label, value }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        padding: "8px 0",
        borderBottom: "1px solid #f3f4f6"
      }}
    >
      <span style={{ color: "#374151", fontWeight: 500 }}>{label}</span>
      <span style={{ color: "#16a34a", fontWeight: 700 }}>{value}</span>
    </div>
  );
}

function Badge({ active, activeText, inactiveText }) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        fontSize: "0.72rem",
        fontWeight: 600,
        padding: "4px 10px",
        borderRadius: "999px",
        background: active ? "#dcfce7" : "#f3f4f6",
        color: active ? "#166534" : "#6b7280",
        border: `1px solid ${active ? "#bbf7d0" : "#e5e7eb"}`
      }}
    >
      {active ? "✓" : "–"} {active ? activeText : inactiveText}
    </span>
  );
}

function Notice({ text }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: "8px",
        textAlign: "left",
        background: "#fff7ed",
        border: "1px solid #fed7aa",
        color: "#9a3412",
        borderRadius: "8px",
        padding: "10px 14px",
        fontSize: "0.8rem",
        marginBottom: "12px"
      }}
    >
      <FiAlertTriangle style={{ marginTop: "2px", flexShrink: 0 }} />
      <span>{text}</span>
    </div>
  );
}

function WarningBox({ children }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: "8px",
        textAlign: "left",
        background: "#fef2f2",
        border: "1px solid #fecaca",
        color: "#991b1b",
        borderRadius: "8px",
        padding: "12px 16px",
        fontSize: "0.85rem",
        marginBottom: "12px"
      }}
    >
      <FiAlertTriangle style={{ marginTop: "2px", flexShrink: 0 }} />
      <span>{children}</span>
    </div>
  );
}

function TaskFeedback({ title, result }) {
  if (!result) return null;

  const notes = [
    result.taskAchievement?.offTopic
      ? { label: "⚠ Off-Topic", text: "This response does not appear to fully address the task prompt." }
      : null,
    { label: "Task Achievement", text: result.taskAchievement?.note },
    result.taskAchievement?.ideaDevelopment
      ? { label: "Idea Development", text: result.taskAchievement.ideaDevelopment.note }
      : null,
    result.taskAchievement?.exampleQuality
      ? { label: "Example Quality", text: result.taskAchievement.exampleQuality.note }
      : null,
    result.taskAchievement?.positionConsistency
      ? { label: "Position Consistency", text: result.taskAchievement.positionConsistency.note }
      : null,
    result.taskAchievement?.task1Details
      ? {
          label: "Task 1 Analysis (key features / grouping / trends / stages)",
          text: result.taskAchievement.task1Specific
        }
      : null,
    { label: "Coherence & Cohesion", text: result.coherence?.note },
    result.coherence?.topicSentences
      ? { label: "Topic Sentences", text: result.coherence.topicSentences.note }
      : null,
    result.coherence?.paragraphUnity
      ? { label: "Paragraph Unity", text: result.coherence.paragraphUnity.note }
      : null,
    { label: "Lexical Resource", text: result.lexical?.note },
    result.lexical?.collocation
      ? { label: "Collocation & Precision", text: result.lexical.collocation.note }
      : null,
    result.lexical?.synonymVariety
      ? { label: "Synonym Variety", text: result.lexical.synonymVariety.note }
      : null,
    { label: "Grammatical Range & Accuracy", text: result.grammar?.note },
    result.grammar?.grammarRange
      ? { label: "Grammar Range (structure variety)", text: result.grammar.grammarRange.note }
      : null,
    result.grammar?.complexSentences
      ? { label: "Complex Sentences", text: result.grammar.complexSentences.note }
      : null,
    result.template?.isTemplate
      ? {
          label: "⚠ Template Match",
          text: `Closely matches a known template essay (${(result.template.similarity * 100).toFixed(0)}% similarity).`
        }
      : null,
    result.plagiarism?.isPlagiarized
      ? {
          label: "⚠ Plagiarism Match",
          text: `Closely matches other existing text (${(result.plagiarism.similarity * 100).toFixed(0)}% similarity) found via similarity search.`
        }
      : null
  ].filter((n) => n && n.text);

  return (
    <div
      style={{
        background: "#f9fafb",
        border: "1px solid #e5e7eb",
        borderRadius: "10px",
        padding: "14px 16px"
      }}
    >
      <h4 style={{ margin: "0 0 8px 0", fontSize: "0.9rem", color: "#111827", fontWeight: 700 }}>
        {title}
      </h4>

      <ul
        style={{
          margin: 0,
          paddingLeft: "18px",
          display: "flex",
          flexDirection: "column",
          gap: "6px"
        }}
      >
        {notes.map((n, i) => (
          <li key={i} style={{ fontSize: "0.78rem", color: "#374151", lineHeight: 1.4 }}>
            <strong>{n.label}:</strong> {n.text}
          </li>
        ))}
      </ul>

      <ReferenceChecklist reference={result.taskAchievement?.reference} />
    </div>
  );
}

const CHECK_MARK = {
  covered: { icon: "✓", color: "#16a34a", label: "covered" },
  partial: { icon: "◐", color: "#d97706", label: "partly covered" },
  missing: { icon: "✗", color: "#dc2626", label: "not found" }
};

// Content coverage against the saved reference points for this question.
function ReferenceChecklist({ reference }) {
  if (!reference) return null;

  const items = reference.checklist.filter((c) => c.required || c.status === "covered");
  const pct = Math.round(reference.contentCoverage * 100);

  return (
    <div style={{ marginTop: "12px", borderTop: "1px solid #e5e7eb", paddingTop: "10px" }}>
      <h5 style={{ margin: "0 0 6px 0", fontSize: "0.82rem", color: "#111827", fontWeight: 700 }}>
        Content checklist: {pct}% of expected points found
      </h5>

      <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: "4px" }}>
        {items.map((c) => {
          const mark = CHECK_MARK[c.status] || CHECK_MARK.missing;
          return (
            <li key={c.id} style={{ fontSize: "0.76rem", color: "#374151", lineHeight: 1.4, display: "flex", gap: "6px" }}>
              <span aria-label={mark.label} title={mark.label} style={{ color: mark.color, fontWeight: 700, flexShrink: 0 }}>
                {mark.icon}
              </span>
              <span>
                {c.description}
                {c.detected ? ` (detected: ${c.detected})` : ""}
                {c.hint ? <em style={{ color: "#6b7280" }}> {c.hint}</em> : null}
              </span>
            </li>
          );
        })}
      </ul>

      {reference.figures?.applicable && reference.figures.unrecognised.length > 0 && (
        <p style={{ fontSize: "0.76rem", color: "#b45309", margin: "8px 0 0 0" }}>
          Figures to double-check: {reference.figures.unrecognised.map((u) => u.value).join(", ")}
        </p>
      )}

      {reference.arguments && reference.arguments.covered.length > 0 && (
        <p style={{ fontSize: "0.76rem", color: "#374151", margin: "8px 0 0 0" }}>
          <strong>Arguments recognised:</strong> {reference.arguments.covered.map((a) => a.label).join("; ")}
        </p>
      )}

      {reference.feedback.length > 0 && (
        <ul style={{ margin: "8px 0 0 0", paddingLeft: "18px" }}>
          {reference.feedback.map((f, i) => (
            <li key={i} style={{ fontSize: "0.76rem", color: "#374151", lineHeight: 1.4 }}>
              {f}
            </li>
          ))}
        </ul>
      )}

      <p style={{ fontSize: "0.7rem", color: "#9ca3af", margin: "8px 0 0 0" }}>{reference.disclaimer}</p>
    </div>
  );
}